import React, { useState, useMemo, useEffect } from 'react';
import { BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, ResponsiveContainer, Cell } from 'recharts';
import { Student, AgendaEntry, AttendanceRecord, AssessmentGrade, SasGrade, Teacher, ActivityType, LearningObjective, FormativeGradeTP, FormativeLevel } from '../types.ts';
import { ArrowUpDown, ChevronLeft, ChevronRight, GraduationCap, Calculator, Percent, Download, ArrowRight, Table as TableIcon, Info, Users, BarChart3, Settings2, FileSpreadsheet, LayoutGrid, Eye, EyeOff, Printer, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getThemeById } from '../src/themeConfig.ts';

const SasInput = ({ initialValue, onSave }: { initialValue: number, onSave: (val: number) => void }) => {
  const [value, setValue] = useState(initialValue.toString());

  React.useEffect(() => {
    // Only update outer value if it significantly differs from our internal state 
    // (meaning it was updated from outside, not from our own keystrokes).
    const numericStrVal = parseFloat(value);
    if (!isNaN(numericStrVal) && numericStrVal !== initialValue && value !== initialValue.toString()) {
       setValue(initialValue.toString());
    }
  }, [initialValue]);

  return (
    <input
      type="number"
      step="any"
      className="w-16 px-1 py-1 text-center bg-transparent border border-transparent hover:border-slate-200 dark:hover:border-slate-700 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 text-amber-600 font-bold rounded outline-none transition-all mx-auto cursor-text placeholder-transparent"
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        const newSasScore = e.target.value === '' ? 0 : parseFloat(e.target.value);
        onSave(newSasScore);
      }}
      onBlur={() => {
        const numeric = parseFloat(value);
        if (isNaN(numeric)) {
          setValue('0');
          onSave(0);
        } else {
          setValue(numeric.toString());
        }
      }}
    />
  );
};

interface NilaiRaporProps {
  students: Student[];
  agendas: AgendaEntry[];
  attendanceRecords: AttendanceRecord[];
  assessmentGrades: AssessmentGrade[];
  sasGrades: SasGrade[];
  activeUser: Teacher;
  objectives: LearningObjective[];
  formativeGradesTP: FormativeGradeTP[];
  schoolSettings: any;
  onUpdateTeacher?: (updated: Teacher) => void;
  onStudentClick?: (student: Student) => void;
  onUpdateSasGrades?: (grades: SasGrade[]) => void;
  onUpdateAssessmentGrades?: (grades: AssessmentGrade[]) => void;
  onUpdateFormativeTP?: (grades: FormativeGradeTP[]) => void;
  onStudentRecapClick?: (student: Student) => void;
  selectedStudentId?: string;
  appTheme?: string;
}

const NilaiRaporView: React.FC<NilaiRaporProps> = ({ 
  students, 
  agendas, 
  attendanceRecords,
  assessmentGrades, 
  sasGrades: sasGradesProp,
  activeUser, 
  objectives, 
  formativeGradesTP,
  schoolSettings,
  onUpdateTeacher,
  onStudentClick,
  onUpdateSasGrades,
  onUpdateAssessmentGrades,
  onUpdateFormativeTP,
  onStudentRecapClick,
  selectedStudentId,
  appTheme = 'wzc-themes'
}) => {
  const currentTheme = useMemo(() => getThemeById(appTheme), [appTheme]);
  const [activeTab, setActiveTab] = useState<'akhir' | 'rekap' | 'rekapFormatif' | 'rangking'>('akhir');
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [isSavingFinalGrades, setIsSavingFinalGrades] = useState(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);

  const handleUpdateSasGrade = (studentId: string, newScore: number) => {
    if (!onUpdateSasGrades || !selectedClass) return;

    const date = new Date().toISOString().split('T')[0];
    const finalData = calculateStudentFinal(studentId, selectedClass);
    const nilaiTh = finalData.nilaiTh ?? 0;
    const nilaiCatatan = finalData.nilaiCatatan ?? 0;
    let computedEvaluasi = Math.round((((nilaiTh + nilaiCatatan) / 2) * (weightDaily / 100)) + (newScore * (weightSas / 100)));
    if (computedEvaluasi < newScore) {
      computedEvaluasi = Math.min(100, newScore + 1);
    }
    const nilaiRapor = finalData.final;

    const existingIndex = (sasGradesProp || []).findIndex(g =>
      g.studentId === studentId &&
      g.class === selectedClass &&
      g.subject === activeUser.subject
    );

    let updated: SasGrade[] = [...(sasGradesProp || [])];

    if (existingIndex >= 0) {
      const oldObj = updated[existingIndex];
      updated[existingIndex] = {
        ...oldObj,
        score: newScore,
        nilaiEvaluasi: computedEvaluasi,
        nilaiRapor,
        nilaiTh,
        nilaiCatatan,
        date,
        updatedAt: Date.now()
      };
    } else {
      updated.push({
        id: `${studentId}-${activeUser.subject}-${date}`,
        studentId,
        score: newScore,
        nilaiEvaluasi: computedEvaluasi,
        nilaiRapor,
        nilaiTh,
        nilaiCatatan,
        class: selectedClass,
        subject: activeUser.subject,
        teacherId: activeUser.id,
        date,
        createdAt: Date.now(),
        updatedAt: Date.now()
      });
    }

    onUpdateSasGrades(updated);
  };

  const handleSaveFinalGradesToFirebase = async () => {
    if (!selectedClass || !onUpdateSasGrades) return;
    setIsSavingFinalGrades(true);
    try {
      const date = new Date().toISOString().split('T')[0];
      const studentsInClass = (students || []).filter(s => s.class === selectedClass && s.status === 'Aktif');

      let currentSasGrades = [...(sasGradesProp || [])];

      studentsInClass.forEach(student => {
        const finalData = calculateStudentFinal(student.id, selectedClass);
        const evalScore = typeof finalData.sas === 'number' ? finalData.sas : 0;
        const rawScore = finalData.rawSasScore ?? evalScore;
        const nilaiRapor = finalData.final;

        const existingIndex = currentSasGrades.findIndex(g =>
          g.studentId === student.id &&
          g.class === selectedClass &&
          g.subject === activeUser.subject
        );

        if (existingIndex >= 0) {
          currentSasGrades[existingIndex] = {
            ...currentSasGrades[existingIndex],
            score: rawScore,
            nilaiEvaluasi: evalScore,
            nilaiRapor,
            nilaiTh: finalData.nilaiTh ?? 0,
            nilaiCatatan: finalData.nilaiCatatan ?? 0,
            date,
            updatedAt: Date.now()
          };
        } else {
          currentSasGrades.push({
            id: `${student.id}-${activeUser.subject}-${date}`,
            studentId: student.id,
            score: rawScore,
            nilaiEvaluasi: evalScore,
            nilaiRapor,
            nilaiTh: finalData.nilaiTh ?? 0,
            nilaiCatatan: finalData.nilaiCatatan ?? 0,
            class: selectedClass,
            subject: activeUser.subject,
            teacherId: activeUser.id,
            date,
            createdAt: Date.now(),
            updatedAt: Date.now()
          });
        }
      });

      console.log('Saving Tabel Hasil Akhir Nilai Rapor & SAS to Firebase:', currentSasGrades);
      await onUpdateSasGrades(currentSasGrades);

      if (onUpdateAssessmentGrades && assessmentGrades) {
        await onUpdateAssessmentGrades([...assessmentGrades]);
      }

      setSaveSuccessMsg('Berhasil! Data Tabel Hasil Akhir Nilai Rapor tersimpan ke Firebase.');
      setTimeout(() => setSaveSuccessMsg(null), 5000);
    } catch (error) {
      console.error('Gagal menyimpan Nilai Rapor ke Firebase:', error);
      alert('Gagal menyimpan data ke Firebase. Silakan coba lagi.');
    } finally {
      setIsSavingFinalGrades(false);
    }
  };
  
  const handleUpdateAssessScore = (studentId: string, tpId: string, activityType: ActivityType, newScore: number) => {
    if (!onUpdateAssessmentGrades || !selectedClass) return;
    
    // Find all matching AssessmentGrade objects by criteria
    const matchingGrades = assessmentGrades.filter(g => 
        (g.class === selectedClass || g.class === 'Semua Kelas') && 
        g.tpId === tpId &&
        g.activityType === activityType
    );

    let updatedGrades = [...assessmentGrades];
    
    if (matchingGrades.length > 0) {
      // Update ALL matching grades for this student to keep averages and lists fully synchronized
      matchingGrades.forEach(matching => {
        const gradeIndex = updatedGrades.findIndex(g => g.id === matching.id);
        if (gradeIndex >= 0) {
          updatedGrades[gradeIndex] = {
            ...updatedGrades[gradeIndex],
            scores: {
              ...(updatedGrades[gradeIndex].scores || {}),
              [studentId]: newScore
            }
          };
        }
      });
    } else {
      // Create new grade entry if it doesn't exist
      const targetId = `${selectedClass}::${tpId}::${activityType}`;
      updatedGrades.push({
        id: targetId,
        class: selectedClass,
        tpId,
        activityType,
        assessmentType: 'Sumatif per TP',
        date: new Date().toISOString().split('T')[0],
        scores: { [studentId]: newScore }
      });
    }
    
    onUpdateAssessmentGrades(updatedGrades);
  };

  const EditableScore = ({ initialValue, studentId, tpId, activityType }: { initialValue: number | null, studentId: string, tpId: string, activityType: ActivityType }) => {
    const [value, setValue] = useState(initialValue !== null ? initialValue.toString() : "");

    useEffect(() => {
        setValue(initialValue !== null ? initialValue.toString() : "");
    }, [initialValue]);

    return (
        <input
            type="number"
            className="w-9 sm:w-10 text-center bg-transparent border-b border-slate-300 focus:border-indigo-500 outline-none font-mono text-xs sm:text-sm font-black text-indigo-600"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => {
                const num = parseFloat(value);
                if (!isNaN(num)) {
                    handleUpdateAssessScore(studentId, tpId, activityType, num);
                } else if (value === "") {
                    setValue(initialValue !== null ? initialValue.toString() : "");
                } else {
                    setValue(initialValue !== null ? initialValue.toString() : "");
                }
            }}
        />
    )
  }
  
  useEffect(() => {
    if (selectedStudentId && students.length > 0) {
      const student = students.find(s => s.id === selectedStudentId);
      if (student) {
        setSelectedClass(student.class);
        setActiveTab('akhir');
        
        // Scroll to the student after a slight delay
        setTimeout(() => {
            const studentRow = document.getElementById(`student-row-${student.id}`);
            if (studentRow) {
                studentRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
                studentRow.classList.add('bg-indigo-50', 'dark:bg-indigo-900/40');
                setTimeout(() => {
                    studentRow.classList.remove('bg-indigo-50', 'dark:bg-indigo-900/40');
                }, 3000);
            }
        }, 300); // Increased delay slightly to allow DOM to render
      }
    }
  }, [selectedStudentId]); // Excluded students from deps so it only runs when ID changes
  const [isSavingWeights, setIsSavingWeights] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: 'name' | 'total' | 'sumatif' | 'praktikum' | 'sas' | 'final' | 'roundedFinal' | 'alpa', direction: 'asc' | 'desc' }>({ key: 'name', direction: 'asc' });
  const [rangkingSortConfig, setRangkingSortConfig] = useState<{ key: 'name' | 'sas' | 'final' | 'roundedFinal', direction: 'asc' | 'desc' }>({ key: 'final', direction: 'desc' });
  
  const [viewingStudentPopup, setViewingStudentPopup] = useState<Student | null>(null);
  const [expandedStudents, setExpandedStudents] = useState<Record<string, boolean>>({});

  const getStudentNameColor = (gender: 'Laki-laki' | 'Perempuan') => {
    return gender === 'Laki-laki' ? 'text-blue-600 dark:text-blue-400' : 'text-pink-600 dark:text-pink-400';
  };
  
  const [weights, setWeights] = useState({ 
    sumatif: activeUser.raporSumatifWeight ?? 40, 
    praktikum: activeUser.raporPraktikumWeight ?? 30,
    sas: activeUser.raporSasWeight ?? 30 
  });

  const myClasses = useMemo(() => activeUser.assignedClasses.sort(), [activeUser]);

  const [weightDaily, setWeightDaily] = useState<number>(() => {
    return activeUser.weightDaily ?? (localStorage.getItem('grading_weight_daily') ? Number(localStorage.getItem('grading_weight_daily')) : 20);
  });
  const [weightSas, setWeightSas] = useState<number>(() => {
    return activeUser.weightSas ?? (localStorage.getItem('grading_weight_sas') ? Number(localStorage.getItem('grading_weight_sas')) : 80);
  });

  useEffect(() => {
    setWeights({ 
      sumatif: activeUser.raporSumatifWeight ?? 40, 
      praktikum: activeUser.raporPraktikumWeight ?? 30,
      sas: activeUser.raporSasWeight ?? 30 
    });
    if (activeUser.weightDaily !== undefined) setWeightDaily(activeUser.weightDaily);
    else {
      const savedDaily = localStorage.getItem('grading_weight_daily');
      if (savedDaily) setWeightDaily(Number(savedDaily));
    }
    if (activeUser.weightSas !== undefined) setWeightSas(activeUser.weightSas);
    else {
      const savedSas = localStorage.getItem('grading_weight_sas');
      if (savedSas) setWeightSas(Number(savedSas));
    }
  }, [activeUser.raporSumatifWeight, activeUser.raporPraktikumWeight, activeUser.raporSasWeight, activeUser.weightDaily, activeUser.weightSas, activeTab]);

  const allNotebookData = useMemo(() => {
    const data: Record<string, { entries: Record<string, { jumlahTtd: number; notes: string }>; maxTtd: number }> = {};
    if (!activeUser?.id) return data;
    
    myClasses.forEach(cls => {
      try {
        const storageKey = `smpn1_notebook_rekap_${activeUser.id}_${cls}`;
        const saved = localStorage.getItem(storageKey);
        let entries: Record<string, { jumlahTtd: number; notes: string }> = {};
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed && typeof parsed === 'object') {
            if (parsed.entries) entries = parsed.entries;
            else entries = parsed;
          }
        }
        
        const classStudents = (students || []).filter(s => s.class === cls && s.status === 'Aktif');
        let maxTtd = 0;
        classStudents.forEach(student => {
          const entry = entries[student.id] || { jumlahTtd: 0, notes: '' };
          const ttd = entry.jumlahTtd || 0;
          if (ttd > maxTtd) {
            maxTtd = ttd;
          }
        });
        
        data[cls] = { entries, maxTtd };
      } catch (err) {
        console.error('Error loading notebook data for ' + cls, err);
        data[cls] = { entries: {}, maxTtd: 0 };
      }
    });
    return data;
  }, [myClasses, activeUser?.id, students]);

  const normalizeClass = (cls?: string) => {
    if (!cls) return '';
    let str = cls.toString().trim().toUpperCase().replace(/\s+/g, '');
    str = str.replace(/^IX/, '9').replace(/^VIII/, '8').replace(/^VII/, '7');
    return str;
  };
  
  // State untuk filter rekap formatif
  const [rekapFormatifClass, setRekapFormatifClass] = useState<string>(myClasses[0] || '');
  const [batasCapaian, setBatasCapaian] = useState<number>(80);

  const handleWeightChange = (key: keyof typeof weights, val: string) => {
    const num = parseInt(val) || 0;
    setWeights(prev => ({ ...prev, [key]: num }));
  };

  const handleSaveWeights = async () => {
    if (!onUpdateTeacher) return;
    setIsSavingWeights(true);
    try {
      const updatedTeacher = {
        ...activeUser,
        raporSumatifWeight: weights.sumatif,
        raporPraktikumWeight: weights.praktikum,
        raporSasWeight: weights.sas
      };
      await onUpdateTeacher(updatedTeacher);
    } catch (error) {
      console.error("Failed to save weights:", error);
    } finally {
      setIsSavingWeights(false);
    }
  };

  const calculateStudentFinal = (studentId: string, targetClass: string) => {
    const relevantGrades = (assessmentGrades || []).filter(g => g.class === targetClass || g.class === 'Semua Kelas');
    const relevantSasGrades = (sasGradesProp || []).filter(g => (g.class === targetClass || g.class === 'Semua Kelas') && g.studentId === studentId && g.subject === activeUser.subject);
    
    const tpGrades: Record<string, { sumatif: number[], praktikum: number[] }> = {};
    const sasScores: number[] = relevantSasGrades.map(g => g.score);

    relevantGrades.forEach(g => {
      const score = (g.scores || {})[studentId];
      if (score === undefined || score === null) return;

      if (g.activityType === ActivityType.SAS) {
        sasScores.push(score);
      } else if (g.tpId) {
        const tp = objectives.find(o => o.id === g.tpId);
        if (!tp) return;

        if (!tpGrades[g.tpId]) {
          tpGrades[g.tpId] = { sumatif: [], praktikum: [] };
        }
        
        if (g.activityType === ActivityType.SUMATIF && tp.isSummative) {
          tpGrades[g.tpId].sumatif.push(score);
        } else if (g.activityType === ActivityType.PRAKTIKUM && tp.isPracticum) {
          tpGrades[g.tpId].praktikum.push(score);
        }
      }
    });

    const tpSumatifMap: Record<string, number> = {};
    const tpPraktikumMap: Record<string, number> = {};
    const sumatifAverages: number[] = [];
    const praktikumAverages: number[] = [];

    const finalSasScore = sasScores.length > 0 ? sasScores[sasScores.length - 1] : 0;

    Object.entries(tpGrades).forEach(([tpId, data]) => {
      if (data.sumatif.length > 0) {
        const sum = data.sumatif.reduce((a, b) => a + b, 0);
        const avg = sum / data.sumatif.length;
        tpSumatifMap[tpId] = avg;
        sumatifAverages.push(avg);
      }
      if (data.praktikum.length > 0) {
        const sum = data.praktikum.reduce((a, b) => a + b, 0);
        const avg = sum / data.praktikum.length;
        tpPraktikumMap[tpId] = avg;
        praktikumAverages.push(avg);
      }
    });

    const avgSumatif = sumatifAverages.length > 0 ? sumatifAverages.reduce((a, b) => a + b, 0) / sumatifAverages.length : 0;
    const avgPraktikum = praktikumAverages.length > 0 ? praktikumAverages.reduce((a, b) => a + b, 0) / praktikumAverages.length : 0;
    
    const classQuizGrades = (assessmentGrades || []).filter(g => 
      normalizeClass(g.class) === normalizeClass(targetClass) && 
      ((g.activityType as any) === 'KBM dan Quiz' || 
       (g.activityType as any) === 'Pembelajaran dan Asesmen' || 
       (g.activityType as any) === 'KBM dan Asesmen Harian' || 
       g.activityType === ActivityType.KBM_DAN_QUIZ || 
       g.activityType === ActivityType.KBM)
    );
    const totalQuizAgendas = classQuizGrades.length;

    let sumQuizScores = 0;
    classQuizGrades.forEach(g => {
      const score = g.scores[studentId];
      const num = Number(score);
      if (!isNaN(num) && score !== undefined && score !== null && String(score) !== '') {
        sumQuizScores += num;
      }
    });
    const nilaiTh = totalQuizAgendas > 0 ? Math.round(sumQuizScores / totalQuizAgendas) : 0;

    const notebookData = allNotebookData[targetClass] || { entries: {}, maxTtd: 0 };
    const { entries: sasNotebookEntries, maxTtd: sasNotebookMaxTtd } = notebookData;
    const ttd = sasNotebookEntries[studentId]?.jumlahTtd || 0;
    const nilaiCatatan = sasNotebookMaxTtd > 0 ? Math.round((ttd / sasNotebookMaxTtd) * 100) : 0;

    const rawSasScore = sasScores.length > 0 ? sasScores[sasScores.length - 1] : 0;
    let nilaiEvaluasi = Math.round((((nilaiTh + nilaiCatatan) / 2) * (weightDaily / 100)) + (rawSasScore * (weightSas / 100)));
    if (nilaiEvaluasi < rawSasScore) {
      nilaiEvaluasi = Math.min(100, rawSasScore + 1);
    }

    // Calculate raw total sum of all scores for 'Jumlah'
    let totalSum = 0;
    relevantGrades.forEach(g => {
        const s = g.scores[studentId];
        if (s !== undefined && s !== null) totalSum += s;
    });
    totalSum += nilaiEvaluasi;

    const final = (avgSumatif * weights.sumatif / 100) + (avgPraktikum * weights.praktikum / 100) + (nilaiEvaluasi * weights.sas / 100);
    
    return { 
      sumatif: parseFloat(avgSumatif.toFixed(2)), 
      praktikum: parseFloat(avgPraktikum.toFixed(2)),
      sas: typeof nilaiEvaluasi === 'number' ? parseFloat(nilaiEvaluasi.toFixed(2)) : nilaiEvaluasi,
      rawSasScore,
      nilaiTh,
      nilaiCatatan, 
      final: parseFloat(final.toFixed(2)),
      roundedFinal: Math.round(final),
      total: parseFloat(totalSum.toFixed(2)),
      tpSumatifMap,
      tpPraktikumMap
    };
  };

  const handleSort = (key: 'name' | 'total' | 'sumatif' | 'praktikum' | 'sas' | 'final' | 'roundedFinal' | 'alpa') => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleRangkingSort = (key: 'name' | 'sas' | 'final' | 'roundedFinal') => {
    setRangkingSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const sortedStudents = useMemo(() => {
    if (!selectedClass) return [];
    
    const studentsInClass = (students || []).filter(s => s.class === selectedClass && s.status === 'Aktif');
    
    if (sortConfig.key === 'name') {
      const sorted = [...studentsInClass].sort((a, b) => a.name.localeCompare(b.name));
      return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
    }
    
    const studentsWithScores = studentsInClass.map(s => {
        const alpaCount = attendanceRecords?.filter(r => r.studentId === s.id && r.class === selectedClass && r.status === 'Alpa').length || 0;
        return {
            student: s,
            finalData: calculateStudentFinal(s.id, selectedClass),
            alpaCount
        };
    });
    
    return studentsWithScores.sort((a, b) => {
      const { key, direction } = sortConfig;
      let valA: number = 0;
      let valB: number = 0;
      
      if (key === 'alpa') {
        valA = a.alpaCount;
        valB = b.alpaCount;
      } else if (key === 'total') {
        valA = a.finalData.total;
        valB = b.finalData.total;
      } else if (key === 'sumatif') {
        valA = a.finalData.sumatif;
        valB = b.finalData.sumatif;
      } else if (key === 'praktikum') {
        valA = a.finalData.praktikum;
        valB = b.finalData.praktikum;
      } else if (key === 'sas') {
        valA = a.finalData.sas;
        valB = b.finalData.sas;
      } else if (key === 'final') {
        valA = a.finalData.final;
        valB = b.finalData.final;
      } else if (key === 'roundedFinal') {
        valA = a.finalData.roundedFinal;
        valB = b.finalData.roundedFinal;
      }
      
      if (valA === valB) {
        return a.student.name.localeCompare(b.student.name);
      }
      
      return direction === 'asc' ? valA - valB : valB - valA;
    }).map(item => item.student);
  }, [selectedClass, students, sortConfig, assessmentGrades, weights, sasGradesProp, objectives, allNotebookData, weightDaily, weightSas, attendanceRecords]);

  const classAnalysis = useMemo(() => {
    if (!selectedClass) return { classAverage: 0, targetAchievementCount: 0, studentCount: 0 };
    const studentsInClass = (students || []).filter(s => s.class === selectedClass && s.status === 'Aktif');
    if (studentsInClass.length === 0) return { classAverage: 0, targetAchievementCount: 0, studentCount: 0 };
    let totalFinalScore = 0, targetAchievementCount = 0;
    const targetThreshold = batasCapaian;
    studentsInClass.forEach(student => {
      const { final: studentFinal } = calculateStudentFinal(student.id, selectedClass);
      totalFinalScore += studentFinal;
      if (studentFinal >= targetThreshold) targetAchievementCount++;
    });
    return { classAverage: parseFloat((totalFinalScore / studentsInClass.length).toFixed(1)), targetAchievementCount, studentCount: studentsInClass.length };
  }, [selectedClass, students, assessmentGrades, weights, batasCapaian, allNotebookData, weightDaily, weightSas]);

  const uniqueTPs = useMemo(() => {
    if (!selectedClass) return [];
    
    // Only show active learning objectives for this subject that have isSummative or isPracticum enabled
    return (objectives || [])
      .filter(o => Boolean(o.isSummative) || Boolean(o.isPracticum))
      .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
  }, [selectedClass, objectives]);

  const rekapitulasiData = useMemo(() => {
    const targetThreshold = batasCapaian;
    return myClasses.map(cls => {
        const studentsInClass = (students || []).filter(s => s.class === cls && s.status === 'Aktif');
        if (studentsInClass.length === 0) return { className: cls, studentCount: 0, classAverage: 0, highestScore: 0, lowestScore: 0, targetAchievementCount: 0 };
        const finalScores = studentsInClass.map(s => calculateStudentFinal(s.id, cls).final);
        const totalScore = finalScores.reduce((acc, score) => acc + score, 0);
        return { className: cls, studentCount: studentsInClass.length, classAverage: parseFloat((totalScore / studentsInClass.length).toFixed(2)), highestScore: Math.max(...finalScores), lowestScore: Math.min(...finalScores), targetAchievementCount: (finalScores || []).filter(score => score >= targetThreshold).length };
    });
  }, [myClasses, students, assessmentGrades, weights, batasCapaian, allNotebookData, weightDaily, weightSas]);

  const addImageToWorksheet = async (workbook: ExcelJS.Workbook, worksheet: ExcelJS.Worksheet, url: string, col: number, row: number) => {
    if (!url) return;
    try {
      let base64Data = '';
      let extension = 'png';
      let imgWidth = 80;
      let imgHeight = 80;
      
      if (url.startsWith('data:image')) {
        base64Data = url.split(',')[1];
        const mime = url.substring(5, url.indexOf(';'));
        extension = mime.split('/')[1] || 'png';
        
        await new Promise<void>((resolve) => {
          const img = new window.Image();
          img.onload = () => {
            imgWidth = img.width;
            imgHeight = img.height;
            resolve();
          };
          img.onerror = () => resolve();
          img.src = url;
        });
      } else {
        const response = await fetch(url);
        const blob = await response.blob();
        base64Data = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.readAsDataURL(blob);
        });
        extension = blob.type.split('/')[1] || 'png';
        
        await new Promise<void>((resolve) => {
          const img = new window.Image();
          img.onload = () => {
            imgWidth = img.width;
            imgHeight = img.height;
            URL.revokeObjectURL(img.src);
            resolve();
          };
          img.onerror = () => {
            URL.revokeObjectURL(img.src);
            resolve();
          };
          img.src = URL.createObjectURL(blob);
        });
      }

      const logoId = workbook.addImage({
        base64: base64Data,
        extension: extension as any,
      });
      
      const targetHeight = 70;
      const targetWidth = (imgWidth / imgHeight) * targetHeight;
      
      worksheet.addImage(logoId, {
        tl: { col: col, row: row },
        ext: { width: targetWidth, height: targetHeight }
      });
    } catch (error) {
      console.error('Failed to add logo to excel:', error);
    }
  };

  const handlePrintRekap = () => {
    if (rekapitulasiData.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Rekap Global Per Rombel - ${activeUser.subject}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
            .kop-logo { width: 80px; height: 80px; object-fit: contain; }
            .kop-text { flex: 1; text-align: center; }
            .kop-gov { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-dept { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-school { font-size: 18px; font-weight: bold; margin: 5px 0; }
            .kop-address { font-size: 10px; font-style: italic; margin: 0; }
            .kop-additional { font-size: 10px; margin: 0; }
            .kop-line-1 { border-bottom: 3px solid black; margin-top: 10px; }
            .kop-line-2 { border-bottom: 1px solid black; margin-top: 2px; margin-bottom: 20px; }
            
            .report-title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; text-transform: uppercase; }
            
            .meta-info { width: 100%; margin-bottom: 20px; font-size: 12px; }
            .meta-info td { padding: 3px 0; }
            
            table.data-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 10px; }
            table.data-table th, table.data-table td { border: 1px solid black; padding: 8px 6px; text-align: center; }
            table.data-table th { background-color: #f0f0f0; font-weight: bold; }
            
            .signature-area { margin-top: 40px; width: 100%; font-size: 12px; }
            .signature-area td { width: 50%; text-align: center; vertical-align: bottom; height: 100px; }
            .signature-name { font-weight: bold; text-decoration: underline; }
            
            @media print {
              @page { size: portrait; margin: 1cm; }
            }
          </style>
        </head>
        <body>
          <div class="kop-container">
            <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
            <div class="kop-text">
              <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
              <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
              <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
              <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
              ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
            </div>
            <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
          </div>
          <div class="kop-line-1"></div>
          <div class="kop-line-2"></div>

          <div class="report-title">REKAPITULASI RATA-RATA NILAI RAPOR PER ROMBEL</div>

          <table class="meta-info">
            <tr>
              <td style="width: 15%">Mata Pelajaran</td><td style="width: 2%">:</td><td style="width: 33%">${activeUser.subject}</td>
              <td style="width: 15%">Semester</td><td style="width: 2%">:</td><td style="width: 33%">${schoolSettings?.semester || '2 (Genap)'}</td>
            </tr>
            <tr>
              <td>Nama Guru</td><td>:</td><td>${activeUser.name}</td>
              <td>Tahun Pelajaran</td><td>:</td><td>${schoolSettings?.academicYear || '2025/2026'}</td>
            </tr>
          </table>

          <table class="data-table">
            <thead>
              <tr>
                <th>NO</th>
                <th>KELAS</th>
                <th>JUMLAH SISWA</th>
                <th>RATA-RATA KELAS</th>
                <th>NILAI TERTINGGI</th>
                <th>NILAI TERENDAH</th>
                <th>TARGET CAPAIAN (${batasCapaian})</th>
              </tr>
            </thead>
            <tbody>
              ${rekapitulasiData.map((d, i) => `
                <tr>
                  <td style="font-weight: bold; font-size: 1rem;">${i + 1}</td>
                  <td style="font-weight: bold; font-size: 1rem;">${d.className}</td>
                  <td style="font-weight: bold; font-size: 1rem;">${d.studentCount}</td>
                  <td style="font-weight: bold; font-size: 1rem;">${d.classAverage}</td>
                  <td style="font-weight: bold; font-size: 1rem;">${d.highestScore}</td>
                  <td style="font-weight: bold; font-size: 1rem;">${d.lowestScore}</td>
                  <td style="font-weight: bold; font-size: 1rem;">${d.targetAchievementCount} / ${d.studentCount} Siswa</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <table class="signature-area">
            <tr>
              <td></td>
              <td>
                Kawali, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Guru Mata Pelajaran
                <br/><br/><br/><br/><br/>
                <div class="signature-name">${activeUser.name}</div>
                <div>NIP. ${activeUser.nip || '-'}</div>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleExportRekap = async () => {
    if (rekapitulasiData.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Rekap Nilai Rapor');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    // Add Logos
    if (schoolSettings.departmentLogoUrl) {
      await addImageToWorksheet(workbook, worksheet, schoolSettings.departmentLogoUrl, 0.2, 0.2);
    }
    if (schoolSettings.logoUrl) {
      await addImageToWorksheet(workbook, worksheet, schoolSettings.logoUrl, 8.5, 0.2);
    }

    // Add Header (Kop)
    worksheet.mergeCells('A1:J1');
    worksheet.getCell('A1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('A1').font = { bold: true, size: 12 };
    worksheet.getCell('A1').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A2:J2');
    worksheet.getCell('A2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A3:J3');
    worksheet.getCell('A3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('A3').font = { bold: true, size: 14 };
    worksheet.getCell('A3').alignment = { horizontal: 'center' };

    worksheet.mergeCells('A4:J4');
    worksheet.getCell('A4').value = schoolSettings.address;
    worksheet.getCell('A4').font = { size: 10 };
    worksheet.getCell('A4').alignment = { horizontal: 'center' };

    let currentRow = 5;
    if (schoolSettings.additionalKopLine) {
      worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = schoolSettings.additionalKopLine;
      worksheet.getCell(`A${currentRow}`).font = { size: 9 };
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
      currentRow++;
    }

    currentRow++; // Empty row
    
    worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'REKAPITULASI NILAI RAPOR GLOBAL PER ROMBEL';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:J${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = `MATA PELAJARAN: ${activeUser.subject.toUpperCase()}`;
    worksheet.getCell(`A${currentRow}`).font = { bold: true };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;
    
    worksheet.addRow([]);
    worksheet.addRow(['', 'Nama Guru', ':', activeUser.name]);
    worksheet.addRow([]);

    // Add Table Header
    const headerRow = worksheet.addRow([
      'NO',
      'KELAS',
      'JUMLAH SISWA',
      'RATA-RATA KELAS',
      'NILAI TERTINGGI',
      'NILAI TERENDAH',
      `CAPAIAN KOMPETENSI (NILAI >= ${batasCapaian}: MENCAPAI KOMPETENSI, < ${batasCapaian}: PERLU PENINGKATAN)`
    ]);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
        cell.font = { bold: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Add Table Data
    rekapitulasiData.forEach((d, idx) => {
        const dataRow = worksheet.addRow([
            idx + 1,
            d.className,
            d.studentCount,
            d.classAverage,
            d.highestScore,
            d.lowestScore,
            `${d.targetAchievementCount} / ${d.studentCount}`
        ]);
        dataRow.eachCell((cell) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
    });

    // Add Signature
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', '', '', '', '', `Kawali, ${today}`]);
    worksheet.addRow(['', '', '', '', '', '', '', '', 'Guru Mata Pelajaran,']);
    worksheet.addRow(['', '', '', '', '', '', '', '', '']);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', '', '', '', '', activeUser.name]);
    worksheet.addRow(['', '', '', '', '', '', '', '', '']);

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    import('file-saver').then(FileSaver => {
        FileSaver.saveAs(blob, `Rekap_Nilai_Rapor_${activeUser.subject}.xlsx`);
    });
  };

  // Logic untuk REKAPITULASI FORMATIF
  const rekapitulasiFormatifData = useMemo(() => {
    if (!rekapFormatifClass) return { tps: [], rows: [] };

    const studentsInClass = (students || []).filter(s => s.class === rekapFormatifClass && s.status === 'Aktif').sort((a,b) => a.name.localeCompare(b.name));
    const subjectObjectives = (objectives || []).filter(o => o.subject === activeUser.subject).sort((a,b) => a.code.localeCompare(b.code));
    
    const rows = studentsInClass.map(student => {
        const levels: Record<string, FormativeLevel> = {};
        subjectObjectives.forEach(tp => {
            const grade = formativeGradesTP.find(g => g.class === rekapFormatifClass && g.tpId === tp.id);
            let lvl = grade?.levels[student.id] || 'Layak'; // Default 'Layak'
            if ((lvl as string) === 'Cakap') {
                lvl = 'Mahir';
            }
            levels[tp.id] = lvl as FormativeLevel;
        });
        return { studentId: student.id, studentName: student.name, levels };
    });

    return { tps: subjectObjectives, rows };
  }, [rekapFormatifClass, students, objectives, formativeGradesTP, activeUser.subject]);

  const rangkingData = useMemo(() => {
    if (activeTab !== 'rangking') return [];
    
    const allStudents = (students || []).filter(s => myClasses.includes(s.class) && s.status === 'Aktif');
    
    const studentsWithScores = allStudents.map(student => {
      const { final, roundedFinal, sas } = calculateStudentFinal(student.id, student.class);
      return { ...student, finalScore: final, roundedFinalScore: roundedFinal, sasScore: sas };
    });

    return studentsWithScores.sort((a, b) => {
      const dir = rangkingSortConfig.direction === 'asc' ? 1 : -1;
      let valA: any = a.finalScore;
      let valB: any = b.finalScore;
      
      if (rangkingSortConfig.key === 'name') {
        valA = a.name;
        valB = b.name;
        return dir * valA.localeCompare(valB);
      } else if (rangkingSortConfig.key === 'sas') {
        valA = (typeof a.sasScore === 'number' ? a.sasScore : 0);
        valB = (typeof b.sasScore === 'number' ? b.sasScore : 0);
      } else if (rangkingSortConfig.key === 'roundedFinal') {
        valA = a.roundedFinalScore;
        valB = b.roundedFinalScore;
      }
      
      if (valA === valB) {
        return a.name.localeCompare(b.name); // always fallback to name asc
      }
      return dir * (valA > valB ? 1 : -1);
    });
  }, [activeTab, students, myClasses, assessmentGrades, weights, sasGradesProp, rangkingSortConfig, allNotebookData, weightDaily, weightSas]);

  const distributionData = useMemo(() => {
    if (rangkingData.length === 0) return [];
    
    const scores = rangkingData.map(s => s.roundedFinalScore);
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    
    const dataMap: Record<number, number> = {};
    for (let score = minScore; score <= maxScore; score++) {
      dataMap[score] = 0;
    }
    
    scores.forEach(score => {
      if (dataMap[score] !== undefined) {
        dataMap[score]++;
      }
    });
    
    return Object.keys(dataMap).map(scoreStr => {
      const score = parseInt(scoreStr, 10);
      let color = '#3b82f6';
      if (score < 70) color = '#f43f5e';
      else if (score < 80) color = '#f59e0b';
      else if (score < 90) color = '#3b82f6';
      else color = '#10b981';
      
      return {
        range: score.toString(),
        count: dataMap[score],
        color
      };
    });
  }, [rangkingData]);
  
  const handlePrintFormatifRekap = () => {
    const { tps, rows } = rekapitulasiFormatifData;
    if (rows.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Rekap Capaian Formatif - ${rekapFormatifClass}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
            .kop-logo { width: 80px; height: 80px; object-fit: contain; }
            .kop-text { flex: 1; text-align: center; }
            .kop-gov { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-dept { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-school { font-size: 18px; font-weight: bold; margin: 5px 0; }
            .kop-address { font-size: 10px; font-style: italic; margin: 0; }
            .kop-additional { font-size: 10px; margin: 0; }
            .kop-line-1 { border-bottom: 3px solid black; margin-top: 10px; }
            .kop-line-2 { border-bottom: 1px solid black; margin-top: 2px; margin-bottom: 20px; }
            
            .report-title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; text-transform: uppercase; }
            
            .meta-info { width: 100%; margin-bottom: 20px; font-size: 12px; }
            .meta-info td { padding: 3px 0; }
            
            table.data-table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 10px; }
            table.data-table th, table.data-table td { border: 1px solid black; padding: 6px 4px; text-align: center; }
            table.data-table th { background-color: #f0f0f0; font-weight: bold; }
            table.data-table td.name { text-align: left; padding-left: 8px; }
            
            .signature-area { margin-top: 40px; width: 100%; font-size: 12px; }
            .signature-area td { width: 50%; text-align: center; vertical-align: bottom; height: 100px; }
            .signature-name { font-weight: bold; text-decoration: underline; }
            
            @media print {
              @page { size: portrait; margin: 1cm; }
            }
          </style>
        </head>
        <body>
          <div class="kop-container">
            <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
            <div class="kop-text">
              <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
              <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
              <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
              <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
              ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
            </div>
            <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
          </div>
          <div class="kop-line-1"></div>
          <div class="kop-line-2"></div>
 
          <div class="report-title">REKAPITULASI CAPAIAN FORMATIF SISWA</div>
 
          <table class="meta-info">
            <tr>
              <td style="width: 15%">Mata Pelajaran</td><td style="width: 2%">:</td><td style="width: 33%">${activeUser.subject}</td>
              <td style="width: 15%">Semester</td><td style="width: 2%">:</td><td style="width: 33%">${schoolSettings?.semester || '2 (Genap)'}</td>
            </tr>
            <tr>
              <td>Nama Guru</td><td>:</td><td>${activeUser.name}</td>
              <td>Tahun Pelajaran</td><td>:</td><td>${schoolSettings?.academicYear || '2025/2026'}</td>
            </tr>
            <tr>
              <td>Kelas</td><td>:</td><td>${rekapFormatifClass}</td>
              <td></td><td></td><td></td>
            </tr>
          </table>
 
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 30px;">NO</th>
                <th style="width: 200px;">NAMA SISWA</th>
                ${tps.map(tp => `<th>${tp.code}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${rows.map((row, i) => {
                const student = students.find(s => s.id === row.studentId);
                const isPerempuan = student?.gender === 'Perempuan';
                return `
                <tr>
                  <td>${i + 1}</td>
                  <td class="name" style="font-weight: bold; ${isPerempuan ? 'color: #e11d48;' : ''}">${row.studentName.toUpperCase()}</td>
                  ${tps.map(tp => {
                    const level = row.levels[tp.id];
                    let color = '';
                    if (level === 'Mahir') color = '#059669';
                    else if (level === 'Layak') color = '#f97316';
                    else if (level === 'Awal Berkembang') color = '#e11d48';
                    
                    return `<td style="color: ${color}; font-weight: bold;">${level}</td>`;
                  }).join('')}
                </tr>
              `}).join('')}
            </tbody>
          </table>
 
          <table class="signature-area">
            <tr>
              <td></td>
              <td>
                Kawali, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Guru Mata Pelajaran
                <br/><br/><br/><br/><br/>
                <div class="signature-name">${activeUser.name}</div>
                <div>NIP. ${activeUser.nip || '-'}</div>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handlePrintAllFormatifRekap = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const subjectObjectives = (objectives || []).filter(o => o.subject === activeUser.subject).sort((a,b) => a.code.localeCompare(b.code));

    let htmlContent = `
      <html>
        <head>
          <title>Rekap Capaian Formatif - Semua Kelas</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            .class-section { page-break-after: always; padding-bottom: 20px; }
            .class-section:last-child { page-break-after: avoid; }
            
            .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
            .kop-logo { width: 80px; height: 80px; object-fit: contain; }
            .kop-text { flex: 1; text-align: center; }
            .kop-gov { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-dept { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-school { font-size: 18px; font-weight: bold; margin: 5px 0; }
            .kop-address { font-size: 10px; font-style: italic; margin: 0; }
            .kop-additional { font-size: 10px; margin: 0; }
            .kop-line-1 { border-bottom: 3px solid black; margin-top: 10px; }
            .kop-line-2 { border-bottom: 1px solid black; margin-top: 2px; margin-bottom: 20px; }
            
            .report-title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; text-transform: uppercase; }
            
            .meta-info { width: 100%; margin-bottom: 20px; font-size: 12px; }
            .meta-info td { padding: 3px 0; }
            
            table.data-table { width: 100%; border-collapse: collapse; font-size: 10px; margin-top: 10px; }
            table.data-table th, table.data-table td { border: 1px solid black; padding: 6px 4px; text-align: center; }
            table.data-table th { background-color: #f0f0f0; font-weight: bold; }
            table.data-table td.name { text-align: left; padding-left: 8px; }
            
            .signature-area { margin-top: 40px; width: 100%; font-size: 12px; }
            .signature-area td { width: 50%; text-align: center; vertical-align: bottom; height: 100px; }
            .signature-name { font-weight: bold; text-decoration: underline; }
            
            @media print {
              @page { size: portrait; margin: 1cm; }
            }
          </style>
        </head>
        <body>
    `;

    myClasses.forEach(cls => {
      const studentsInClass = (students || []).filter(s => s.class === cls && s.status === 'Aktif').sort((a,b) => a.name.localeCompare(b.name));
      if (studentsInClass.length === 0) return;

      const rows = studentsInClass.map(student => {
        const levels: Record<string, FormativeLevel> = {};
        subjectObjectives.forEach(tp => {
          const grade = formativeGradesTP.find(g => g.class === cls && g.tpId === tp.id);
          let lvl = grade?.levels[student.id] || 'Layak'; // Default 'Layak'
          if ((lvl as string) === 'Cakap') {
            lvl = 'Mahir';
          }
          levels[tp.id] = lvl as FormativeLevel;
        });
        return { studentId: student.id, studentName: student.name, levels };
      });

      htmlContent += `
        <div class="class-section">
          <div class="kop-container">
            <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
            <div class="kop-text">
              <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
              <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
              <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
              <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
              ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
            </div>
            <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
          </div>
          <div class="kop-line-1"></div>
          <div class="kop-line-2"></div>

          <div class="report-title">REKAPITULASI CAPAIAN FORMATIF SISWA</div>

          <table class="meta-info">
            <tr>
              <td style="width: 15%">Mata Pelajaran</td><td style="width: 2%">:</td><td style="width: 33%">${activeUser.subject}</td>
              <td style="width: 15%">Semester</td><td style="width: 2%">:</td><td style="width: 33%">${schoolSettings?.semester || '2 (Genap)'}</td>
            </tr>
            <tr>
              <td>Nama Guru</td><td>:</td><td>${activeUser.name}</td>
              <td>Tahun Pelajaran</td><td>:</td><td>${schoolSettings?.academicYear || '2025/2026'}</td>
            </tr>
            <tr>
              <td>Kelas</td><td>:</td><td>${cls}</td>
              <td></td><td></td><td></td>
            </tr>
          </table>

          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 30px;">NO</th>
                <th style="width: 200px;">NAMA SISWA</th>
                ${subjectObjectives.map(tp => `<th>${tp.code}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${rows.map((row, i) => {
                const student = students.find(s => s.id === row.studentId);
                const isPerempuan = student?.gender === 'Perempuan';
                return `
                <tr>
                  <td>${i + 1}</td>
                  <td class="name" style="font-weight: bold; ${isPerempuan ? 'color: #e11d48;' : ''}">${row.studentName.toUpperCase()}</td>
                  ${subjectObjectives.map(tp => {
                    const level = row.levels[tp.id];
                    let color = '';
                    if (level === 'Mahir') color = '#059669';
                    else if (level === 'Layak') color = '#f97316';
                    else if (level === 'Awal Berkembang') color = '#e11d48';
                    
                    return `<td style="color: ${color}; font-weight: bold;">${level}</td>`;
                  }).join('')}
                </tr>
              `}).join('')}
            </tbody>
          </table>

          <table class="signature-area">
            <tr>
              <td></td>
              <td>
                Kawali, ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}<br/>
                Guru Mata Pelajaran
                <br/><br/><br/><br/><br/>
                <div class="signature-name">${activeUser.name}</div>
                <div>NIP. ${activeUser.nip || '-'}</div>
              </td>
            </tr>
          </table>
        </div>
      `;
    });

    htmlContent += `
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 500); };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExportFormatifRekap = async () => {
    const { tps, rows } = rekapitulasiFormatifData;
    if (rows.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Rekap Formatif ${rekapFormatifClass}`);
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    // Add Logos
    if (schoolSettings.departmentLogoUrl) {
      await addImageToWorksheet(workbook, worksheet, schoolSettings.departmentLogoUrl, 0.2, 0.2);
    }
    if (schoolSettings.logoUrl) {
      const lastColIndex = 2 + tps.length;
      await addImageToWorksheet(workbook, worksheet, schoolSettings.logoUrl, lastColIndex - 1.5, 0.2);
    }

    // Add Header (Kop)
    const lastColLetter = worksheet.getColumn(2 + tps.length).letter;
    worksheet.mergeCells(`A1:${lastColLetter}1`);
    worksheet.getCell('A1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('A1').font = { bold: true, size: 12 };
    worksheet.getCell('A1').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A2:${lastColLetter}2`);
    worksheet.getCell('A2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A3:${lastColLetter}3`);
    worksheet.getCell('A3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('A3').font = { bold: true, size: 14 };
    worksheet.getCell('A3').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A4:${lastColLetter}4`);
    worksheet.getCell('A4').value = schoolSettings.address;
    worksheet.getCell('A4').font = { size: 10 };
    worksheet.getCell('A4').alignment = { horizontal: 'center' };

    let currentRow = 5;
    if (schoolSettings.additionalKopLine) {
      worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = schoolSettings.additionalKopLine;
      worksheet.getCell(`A${currentRow}`).font = { size: 9 };
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
      currentRow++;
    }

    currentRow++; // Empty row
    
    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'REKAPITULASI CAPAIAN KOMPETENSI (FORMATIF TP)';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = `KELAS: ${rekapFormatifClass} | MATA PELAJARAN: ${activeUser.subject.toUpperCase()}`;
    worksheet.getCell(`A${currentRow}`).font = { bold: true };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;
    
    worksheet.addRow([]);
    worksheet.addRow(['', 'Nama Guru', ':', activeUser.name]);
    worksheet.addRow([]);

    // Add Table Header
    const headerRow = worksheet.addRow([
      'NO',
      'NAMA SISWA',
      ...tps.map(tp => tp.code.toUpperCase())
    ]);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
        cell.font = { bold: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    // Add Table Data
    rows.forEach((row, idx) => {
        const dataRow = worksheet.addRow([
            idx + 1,
            row.studentName.toUpperCase(),
            ...tps.map(tp => row.levels[tp.id] || '-')
        ]);
        dataRow.eachCell((cell) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
    });

    // Add Signature
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', '', '', '', '', `Kawali, ${today}`]);
    worksheet.addRow(['', '', '', '', '', '', '', '', 'Guru Mata Pelajaran,']);
    worksheet.addRow(['', '', '', '', '', '', '', '', '']);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', '', '', '', '', '', activeUser.name]);
    worksheet.addRow(['', '', '', '', '', '', '', '', '']);

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    import('file-saver').then(FileSaver => {
        FileSaver.saveAs(blob, `Rekap_Formatif_TP_${rekapFormatifClass}_${activeUser.subject}.xlsx`);
    });
  };
  
  const handlePrintClassFinal = () => {
    if (!selectedClass) return;
    const studentsInClass = (students || [])
      .filter(s => s.class === selectedClass && s.status === 'Aktif')
      .sort((a, b) => a.name.localeCompare(b.name));

    // Exclude TPs that have neither summative nor practical scores for all students in the class
    const classTPsWithGrades = uniqueTPs.filter(tp => {
      return studentsInClass.some(student => {
        const finalData = calculateStudentFinal(student.id, selectedClass);
        return finalData.tpSumatifMap[tp.id] !== undefined || finalData.tpPraktikumMap[tp.id] !== undefined;
      });
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

    printWindow.document.write(`
      <html>
        <head>
          <title>Nilai Rapor Akhir - ${selectedClass}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
            .kop-logo { width: 80px; height: 80px; object-fit: contain; }
            .kop-text { flex: 1; text-align: center; }
            .kop-gov { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-dept { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-school { font-size: 18px; font-weight: bold; margin: 5px 0; }
            .kop-address { font-size: 10px; font-style: italic; margin: 0; }
            .kop-additional { font-size: 10px; margin: 0; }
            .kop-line-1 { border-bottom: 3px solid black; margin-top: 10px; }
            .kop-line-2 { border-bottom: 1px solid black; margin-top: 2px; margin-bottom: 20px; }
            
            .report-title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; }
            
            .meta-info { width: 100%; margin-bottom: 20px; font-size: 12px; }
            .meta-info td { padding: 3px 0; }
            
            table.data-table { width: 100%; border-collapse: collapse; font-size: 12px; }
            table.data-table th, table.data-table td { border: 1px solid black; padding: 6px 4px; text-align: center; }
            table.data-table th { background-color: #f0f0f0; font-weight: bold; }
            table.data-table td.name { text-align: left; padding-left: 8px; }
            
            .signature-area { margin-top: 40px; width: 100%; font-size: 12px; }
            .signature-area td { width: 50%; text-align: center; vertical-align: bottom; height: 100px; }
            .signature-name { font-weight: bold; text-decoration: underline; }
            
            .tp-legend { margin-top: 25px; border-top: 1px dashed #ccc; padding-top: 15px; }
            .tp-legend-title { font-size: 11px; font-weight: bold; margin-bottom: 8px; text-transform: uppercase; }
            .tp-table { width: 100%; border-collapse: collapse; font-size: 9px; line-height: 1.4; }
            .tp-table td { vertical-align: top; padding: 2px 0; }
            .tp-code-cell { width: 60px; font-weight: bold; }
            .tp-sep-cell { width: 15px; text-align: center; }
            
            @media print {
              @page { size: portrait; margin: 1cm; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="kop-container">
            <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
            <div class="kop-text">
              <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
              <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
              <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
              <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
              ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
            </div>
            <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
          </div>
          <div class="kop-line-1"></div>
          <div class="kop-line-2"></div>

          <div class="report-title">REKAPITULASI NILAI RAPOR AKHIR SISWA</div>

          <table class="meta-info">
            <tr>
              <td style="width: 15%">Mata Pelajaran</td><td style="width: 2%">:</td><td style="width: 33%">${activeUser.subject}</td>
              <td style="width: 15%">Semester</td><td style="width: 2%">:</td><td style="width: 33%">${schoolSettings?.semester || '2 (Genap)'}</td>
            </tr>
            <tr>
              <td>Nama Guru</td><td>:</td><td>${activeUser.name}</td>
              <td>Tahun Pelajaran</td><td>:</td><td>${schoolSettings?.academicYear || '2025/2026'}</td>
            </tr>
            <tr>
              <td>Kelas</td><td>:</td><td>${selectedClass}</td>
              <td>Bobot Nilai</td><td>:</td><td>S:${weights.sumatif}% | P:${weights.praktikum}% | S.Akhir:${weights.sas}%</td>
            </tr>
          </table>

          <table class="data-table">
            <thead>
              <tr>
                <th rowspan="2">NO</th>
                <th rowspan="2">NAMA SISWA</th>
                <th rowspan="2">NISN</th>
                <th colspan="${classTPsWithGrades.length || 1}">SUMATIF TP</th>
                <th colspan="${classTPsWithGrades.length || 1}">PRAKTIKUM TP</th>
                <th rowspan="2">RATA SUMATIF</th>
                <th rowspan="2">RATA PRAKTIKUM</th>
                <th rowspan="2">SUMATIF AKHIR<br/>(EVALUASI)</th>
                <th rowspan="2">NILAI AKHIR</th>
                <th rowspan="2">NILAI RAPOR</th>
              </tr>
              <tr>
                ${classTPsWithGrades.length > 0 ? classTPsWithGrades.map(tp => `<th>${tp.code}</th>`).join('') : '<th>-</th>'}
                ${classTPsWithGrades.length > 0 ? classTPsWithGrades.map(tp => `<th>${tp.code}</th>`).join('') : '<th>-</th>'}
              </tr>
            </thead>
            <tbody>
              ${studentsInClass.map((student, idx) => {
                const finalData = calculateStudentFinal(student.id, selectedClass);
                return `
                  <tr>
                    <td>${idx + 1}</td>
                    <td class="name">${student.name.toUpperCase()}</td>
                    <td>${student.nisn || student.nis || '-'}</td>
                    ${classTPsWithGrades.length > 0 ? classTPsWithGrades.map(tp => `<td>${finalData.tpSumatifMap[tp.id] !== undefined ? Math.round(finalData.tpSumatifMap[tp.id]) : '-'}</td>`).join('') : '<td>-</td>'}
                    ${classTPsWithGrades.length > 0 ? classTPsWithGrades.map(tp => `<td>${finalData.tpPraktikumMap[tp.id] !== undefined ? Math.round(finalData.tpPraktikumMap[tp.id]) : '-'}</td>`).join('') : '<td>-</td>'}
                    <td>${finalData.sumatif}</td>
                    <td>${finalData.praktikum}</td>
                    <td>${finalData.sas}</td>
                    <td style="${finalData.final < batasCapaian ? 'color: red;' : ''}">${finalData.final}</td>
                          <td className="px-3 py-3 text-center font-black text-rose-500 text-base font-mono border-l border-slate-100 dark:border-slate-800/50">{alpaCount}</td>
                    <td style="font-weight: bold; background-color: #f0f0f0;">${finalData.roundedFinal}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <!-- Statistik Section -->
          <div style="margin-top: 15px; font-size: 11px;">
            <table style="width: 250px; border-collapse: collapse;">
              ${(() => {
                const finalScores = studentsInClass.map(s => calculateStudentFinal(s.id, selectedClass!).final);
                const totalScore = finalScores.reduce((a, b) => a + b, 0);
                const avgScore = finalScores.length > 0 ? (totalScore / finalScores.length).toFixed(1) : 0;
                const high = finalScores.length > 0 ? Math.max(...finalScores).toFixed(2) : '0.00';
                const low = finalScores.length > 0 ? Math.min(...finalScores).toFixed(2) : '0.00';
                const maxScore = studentsInClass.length * 100;
                const classPercentage = maxScore > 0 ? ((totalScore / maxScore) * 100).toFixed(1) : '0.0';
                return `
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Nilai Tertinggi</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${high}</strong></td>
                  </tr>
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Nilai Terendah</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${low}</strong></td>
                  </tr>
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Rata-rata</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${avgScore}</strong></td>
                  </tr>
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Persentase Kelas</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${classPercentage}%</strong></td>
                  </tr>
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Jumlah Siswa</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${studentsInClass.length}</strong></td>
                  </tr>
                `;
              })()}
            </table>
          </div>

          <div style="margin-top: 20px; font-size: 10px; line-height: 1.4; color: #444; border-top: 1px solid #eee; padding-top: 10px;">
            <strong>Mengacu pada:</strong><br/>
            1. Permendikbudristek No. 21 Tahun 2022 tentang Standar Penilaian Pendidikan.<br/>
            2. Panduan Pembelajaran dan Asesmen Kurikulum Merdeka.<br/><br/>
            <strong>Isi aturannya menjelaskan bahwa:</strong><br/>
            1. Penilaian terdiri dari asesmen formatif dan sumatif,<br/>
            2. Guru/satuan pendidikan diberi kewenangan mengolah penilaian,<br/>
            3. Nilai praktikum adalah nilai yang diperoleh peserta didik dari kegiatan praktik atau penerapan langsung suatu keterampilan untuk menilai kemampuan nyata dalam melakukan suatu tugas sesuai tujuan pembelajaran.<br/>
            4. Pembobotan presentase nilai diserahkan kepada guru, MGMP, atau kebijakan satuan pendidikan sesuai karakter mata pelajaran.
          </div>

          <table class="signature-area">
            <tr>
              <td>
              </td>
              <td>
                Kawali, ${today}<br/>
                Guru Mata Pelajaran,<br/><br/><br/><br/>
                <span class="signature-name">${activeUser.name}</span><br/>
              </td>
            </tr>
          </table>
          
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrintRangking = () => {
    if (rangkingData.length === 0) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

    printWindow.document.write(`
      <html>
        <head>
          <title>Rekap Rangking Pararel - ${activeUser.subject}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
            .kop-logo { width: 80px; height: 80px; object-fit: contain; }
            .kop-text { flex: 1; text-align: center; }
            .kop-gov { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-dept { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-school { font-size: 18px; font-weight: bold; margin: 5px 0; }
            .kop-address { font-size: 10px; font-style: italic; margin: 0; }
            .kop-additional { font-size: 10px; margin: 0; }
            .kop-line-1 { border-bottom: 3px solid black; margin-top: 10px; }
            .kop-line-2 { border-bottom: 1px solid black; margin-top: 2px; margin-bottom: 20px; }
            
            .report-title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; }
            
            .meta-info { width: 100%; margin-bottom: 20px; font-size: 12px; }
            .meta-info td { padding: 3px 0; }
            
            table.data-table { width: 100%; border-collapse: collapse; font-size: 10px; }
            table.data-table th, table.data-table td { border: 1px solid black; padding: 6px 4px; text-align: center; }
            table.data-table th { background-color: #f0f0f0; font-weight: bold; }
            table.data-table td.name { text-align: left; padding-left: 15px; }
            
            .signature-area { margin-top: 40px; width: 100%; font-size: 12px; }
            .signature-area td { width: 50%; text-align: center; vertical-align: bottom; height: 100px; }
            .signature-name { font-weight: bold; text-decoration: underline; }
            
            @media print {
              @page { size: portrait; margin: 1cm; }
            }
          </style>
        </head>
        <body>
          <div class="kop-container">
            <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
            <div class="kop-text">
              <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
              <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
              <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
              <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
              ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
            </div>
            <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
          </div>
          <div class="kop-line-1"></div>
          <div class="kop-line-2"></div>
 
          <div class="report-title">REKAPITULASI RANGKING PARALEL</div>
 
          <table class="meta-info">
            <tr>
              <td style="width: 20%">Mata Pelajaran</td><td style="width: 2%">:</td><td>${activeUser.subject}</td>
            </tr>
            <tr>
              <td>Nama Guru</td><td>:</td><td>${activeUser.name}</td>
            </tr>
          </table>
 
          <table class="data-table">
            <thead>
              <tr>
                <th style="width: 60px;">RANK</th>
                <th>NAMA SISWA</th>
                <th style="width: 100px;">KELAS</th>
                <th style="width: 120px;">NILAI S.AKHIR (EVALUASI)</th>
                <th style="width: 120px;">NILAI AKHIR</th>
                <th style="width: 120px;">NILAI RAPOR</th>
              </tr>
            </thead>
            <tbody>
              ${rangkingData.map((s, i) => `
                <tr>
                  <td style="font-weight: bold;">${i + 1}</td>
                  <td class="name" style="font-weight: bold; ${s.gender === 'Perempuan' ? 'color: #e11d48;' : ''}">${s.name.toUpperCase()}</td>
                  <td style="font-weight: bold;">${s.class}</td>
                  <td style="font-weight: bold;">${s.sasScore}</td>
                  <td style="font-weight: bold;">${s.finalScore}</td>
                  <td style="font-weight: bold;">${s.roundedFinalScore}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <table class="signature-area">
            <tr>
              <td>
              </td>
              <td>
                Kawali, ${today}<br/>
                Guru Mata Pelajaran,<br/><br/><br/><br/>
                <span class="signature-name">${activeUser.name}</span><br/>
              </td>
            </tr>
          </table>
          
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleExportRangking = async () => {
    if (rangkingData.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Rekap Rangking Pararel');
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    const totalCols = 4;
    const lastColLetter = 'D';

    // Add Logos
    if (schoolSettings.departmentLogoUrl) {
      await addImageToWorksheet(workbook, worksheet, schoolSettings.departmentLogoUrl, 0.2, 0.2);
    }
    if (schoolSettings.logoUrl) {
      await addImageToWorksheet(workbook, worksheet, schoolSettings.logoUrl, totalCols - 1, 0.2);
    }

    // Add Header (Kop)
    worksheet.mergeCells(`A1:${lastColLetter}1`);
    worksheet.getCell('A1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('A1').font = { bold: true, size: 12 };
    worksheet.getCell('A1').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A2:${lastColLetter}2`);
    worksheet.getCell('A2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A3:${lastColLetter}3`);
    worksheet.getCell('A3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('A3').font = { bold: true, size: 14 };
    worksheet.getCell('A3').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A4:${lastColLetter}4`);
    worksheet.getCell('A4').value = schoolSettings.address;
    worksheet.getCell('A4').font = { size: 10 };
    worksheet.getCell('A4').alignment = { horizontal: 'center' };

    let currentRow = 5;
    if (schoolSettings.additionalKopLine) {
      worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = schoolSettings.additionalKopLine;
      worksheet.getCell(`A${currentRow}`).font = { size: 9 };
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
      currentRow++;
    }

    currentRow++; // Empty row
    
    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'REKAPITULASI RANGKING PARALEL';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;

    worksheet.addRow(['Mata Pelajaran', ':', activeUser.subject]);
    worksheet.addRow(['Nama Guru', ':', activeUser.name]);
    worksheet.addRow([]);

    // Add Table Header
    const headerRow = worksheet.addRow(['RANK', 'NAMA SISWA', 'KELAS', 'NILAI S.AKHIR (EVALUASI)', 'NILAI AKHIR', 'NILAI RAPOR']);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
        cell.font = { bold: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    // Add Table Data
    rangkingData.forEach((s, idx) => {
        const dataRow = worksheet.addRow([
            idx + 1,
            s.name.toUpperCase(),
            s.class,
            s.sasScore,
            s.finalScore,
            s.roundedFinalScore
        ]);
        dataRow.eachCell((cell, colNumber) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            if (colNumber === 2) {
                cell.alignment = { horizontal: 'left' };
            } else {
                cell.alignment = { horizontal: 'center' };
            }
        });
    });

    // Add Signature
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    worksheet.addRow([]);
    worksheet.addRow([]);
    
    worksheet.addRow(['', '', '', `Kawali, ${today}`]);
    worksheet.addRow(['', '', '', 'Guru Mata Pelajaran,']);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(['', '', '', activeUser.name]);

    // Set column widths
    worksheet.columns = [
        { width: 10 }, // RANK
        { width: 40 }, // NAMA SISWA
        { width: 15 }, // KELAS
        { width: 15 }  // NILAI AKHIR
    ];

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const FileSaver = await import('file-saver');
    FileSaver.saveAs(blob, `Rangking_Paralel_${activeUser.subject}.xlsx`);
  };

  const getUniqueTPsForClass = (clsName: string) => {
    // Only return active learning objectives for this subject that have isSummative or isPracticum enabled
    return (objectives || [])
      .filter(o => Boolean(o.isSummative) || Boolean(o.isPracticum))
      .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
  };

  const handlePrintAllClasses = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });

    let htmlContent = `
      <html>
        <head>
          <title>Nilai Rapor Akhir - Semua Kelas</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
            body { font-family: 'Inter', sans-serif; padding: 20px; color: #333; }
            .class-section { page-break-after: always; padding-bottom: 20px; }
            .class-section:last-child { page-break-after: avoid; }
            
            .kop-container { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; text-align: center; }
            .kop-logo { width: 80px; height: 80px; object-fit: contain; }
            .kop-text { flex: 1; text-align: center; }
            .kop-gov { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-dept { font-size: 14px; font-weight: bold; margin: 0; }
            .kop-school { font-size: 18px; font-weight: bold; margin: 5px 0; }
            .kop-address { font-size: 10px; font-style: italic; margin: 0; }
            .kop-additional { font-size: 10px; margin: 0; }
            .kop-line-1 { border-bottom: 3px solid black; margin-top: 10px; }
            .kop-line-2 { border-bottom: 1px solid black; margin-top: 2px; margin-bottom: 20px; }
            
            .report-title { text-align: center; font-size: 16px; font-weight: bold; margin-bottom: 20px; text-decoration: underline; }
            
            .meta-info { width: 100%; margin-bottom: 20px; font-size: 12px; }
            .meta-info td { padding: 3px 0; }
            
            table.data-table { width: 100%; border-collapse: collapse; font-size: 12px; }
            table.data-table th, table.data-table td { border: 1px solid black; padding: 6px 4px; text-align: center; }
            table.data-table th { background-color: #f0f0f0; font-weight: bold; }
            table.data-table td.name { text-align: left; padding-left: 8px; }
            
            .signature-area { margin-top: 40px; width: 100%; font-size: 12px; }
            .signature-area td { width: 50%; text-align: center; vertical-align: bottom; height: 100px; }
            .signature-name { font-weight: bold; text-decoration: underline; }
            
            @media print {
              @page { size: portrait; margin: 1cm; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
    `;

    myClasses.forEach((cls) => {
      const studentsInClass = (students || [])
        .filter(s => s.class === cls && s.status === 'Aktif')
        .sort((a, b) => a.name.localeCompare(b.name));

      if (studentsInClass.length === 0) return;

      const classTPs = getUniqueTPsForClass(cls);
      const classTPsWithGrades = classTPs.filter(tp => {
        return studentsInClass.some(student => {
          const finalData = calculateStudentFinal(student.id, cls);
          return finalData.tpSumatifMap[tp.id] !== undefined || finalData.tpPraktikumMap[tp.id] !== undefined;
        });
      });

      htmlContent += `
        <div class="class-section">
          <div class="kop-container">
            <img src="${schoolSettings.departmentLogoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c5/Logo_Kabupaten_Ciamis.png/800px-Logo_Kabupaten_Ciamis.png'}" class="kop-logo" alt="Logo Dinas" referrerPolicy="no-referrer" />
            <div class="kop-text">
              <p class="kop-gov">${schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS'}</p>
              <p class="kop-dept">${schoolSettings.departmentName || 'DINAS PENDIDIKAN'}</p>
              <p class="kop-school">${schoolSettings.schoolName || 'SMP NEGERI 1 KAWALI'}</p>
              <p class="kop-address">${schoolSettings.address || 'Jl. Raya Kawali No. 1, Kawali, Ciamis, Jawa Barat 46253'}</p>
              ${schoolSettings.additionalKopLine ? `<p class="kop-additional">${schoolSettings.additionalKopLine}</p>` : ''}
            </div>
            <img src="${schoolSettings.logoUrl || 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9c/Logo_Kemdikbud.png/800px-Logo_Kemdikbud.png'}" class="kop-logo" alt="Logo Sekolah" referrerPolicy="no-referrer" />
          </div>
          <div class="kop-line-1"></div>
          <div class="kop-line-2"></div>

          <div class="report-title">REKAPITULASI NILAI RAPOR AKHIR SISWA</div>

          <table class="meta-info">
            <tr>
              <td style="width: 15%">Mata Pelajaran</td><td style="width: 2%">:</td><td style="width: 33%">${activeUser.subject}</td>
              <td style="width: 15%">Semester</td><td style="width: 2%">:</td><td style="width: 33%">${schoolSettings?.semester || '2 (Genap)'}</td>
            </tr>
            <tr>
              <td>Nama Guru</td><td>:</td><td>${activeUser.name}</td>
              <td>Tahun Pelajaran</td><td>:</td><td>${schoolSettings?.academicYear || '2025/2026'}</td>
            </tr>
            <tr>
              <td>Kelas</td><td>:</td><td>${cls}</td>
              <td>Bobot Nilai</td><td>:</td><td>S:${weights.sumatif}% | P:${weights.praktikum}% | S.Akhir:${weights.sas}%</td>
            </tr>
          </table>

          <table class="data-table">
            <thead>
              <tr>
                <th rowspan="2">NO</th>
                <th rowspan="2">NAMA SISWA</th>
                <th rowspan="2">NISN</th>
                <th colspan="${classTPsWithGrades.length || 1}">SUMATIF TP</th>
                <th colspan="${classTPsWithGrades.length || 1}">PRAKTIKUM TP</th>
                <th rowspan="2">RATA SUMATIF</th>
                <th rowspan="2">RATA PRAKTIKUM</th>
                <th rowspan="2">SUMATIF AKHIR<br/>(EVALUASI)</th>
                <th rowspan="2">NILAI AKHIR</th>
                <th rowspan="2">NILAI RAPOR</th>
              </tr>
              <tr>
                ${classTPsWithGrades.length > 0 ? classTPsWithGrades.map(tp => `<th>${tp.code}</th>`).join('') : '<th>-</th>'}
                ${classTPsWithGrades.length > 0 ? classTPsWithGrades.map(tp => `<th>${tp.code}</th>`).join('') : '<th>-</th>'}
              </tr>
            </thead>
            <tbody>
              ${studentsInClass.map((student, idx) => {
                const finalData = calculateStudentFinal(student.id, cls);
                return `
                  <tr>
                    <td>${idx + 1}</td>
                    <td class="name">${student.name.toUpperCase()}</td>
                    <td>${student.nisn || student.nis || '-'}</td>
                    ${classTPsWithGrades.length > 0 ? classTPsWithGrades.map(tp => `<td>${finalData.tpSumatifMap[tp.id] !== undefined ? Math.round(finalData.tpSumatifMap[tp.id]) : '-'}</td>`).join('') : '<td>-</td>'}
                    ${classTPsWithGrades.length > 0 ? classTPsWithGrades.map(tp => `<td>${finalData.tpPraktikumMap[tp.id] !== undefined ? Math.round(finalData.tpPraktikumMap[tp.id]) : '-'}</td>`).join('') : '<td>-</td>'}
                    <td>${finalData.sumatif}</td>
                    <td>${finalData.praktikum}</td>
                    <td>${finalData.sas}</td>
                    <td style="${finalData.final < batasCapaian ? 'color: red;' : ''}">${finalData.final}</td>
                          <td className="px-3 py-3 text-center font-black text-rose-500 text-base font-mono border-l border-slate-100 dark:border-slate-800/50">{alpaCount}</td>
                    <td style="font-weight: bold; background-color: #f0f0f0;">${finalData.roundedFinal}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <!-- Statistik Section -->
          <div style="margin-top: 15px; font-size: 11px;">
            <table style="width: 250px; border-collapse: collapse;">
              ${(() => {
                const finalScores = studentsInClass.map(s => calculateStudentFinal(s.id, cls).final);
                const totalScore = finalScores.reduce((a, b) => a + b, 0);
                const avgScore = finalScores.length > 0 ? (totalScore / finalScores.length).toFixed(1) : 0;
                const high = finalScores.length > 0 ? Math.max(...finalScores).toFixed(2) : '0.00';
                const low = finalScores.length > 0 ? Math.min(...finalScores).toFixed(2) : '0.00';
                const maxScore = studentsInClass.length * 100;
                const classPercentage = maxScore > 0 ? ((totalScore / maxScore) * 100).toFixed(1) : '0.0';
                return `
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Nilai Tertinggi</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${high}</strong></td>
                  </tr>
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Nilai Terendah</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${low}</strong></td>
                  </tr>
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Rata-rata</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${avgScore}</strong></td>
                  </tr>
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Persentase Kelas</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${classPercentage}%</strong></td>
                  </tr>
                  <tr>
                    <td style="text-align: left; padding: 2px 10px 2px 0;">Jumlah Siswa</td>
                    <td style="text-align: left; padding: 2px 0;">: <strong>${studentsInClass.length}</strong></td>
                  </tr>
                `;
              })()}
            </table>
          </div>

          <table class="signature-area">
            <tr>
              <td></td>
              <td>
                Kawali, ${today}<br/>
                Guru Mata Pelajaran,<br/><br/><br/><br/>
                <span class="signature-name">${activeUser.name}</span><br/>
              </td>
            </tr>
          </table>
        </div>
      `;
    });

    htmlContent += `
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 500);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleExportAllClasses = async () => {
    const workbook = new ExcelJS.Workbook();

    for (const cls of myClasses) {
      const studentsInClass = (students || [])
        .filter(s => s.class === cls && s.status === 'Aktif')
        .sort((a, b) => a.name.localeCompare(b.name));

      if (studentsInClass.length === 0) continue;

      const classTPs = getUniqueTPsForClass(cls);
      const classTPsWithGrades = classTPs.filter(tp => {
        return studentsInClass.some(student => {
          const finalData = calculateStudentFinal(student.id, cls);
          return finalData.tpSumatifMap[tp.id] !== undefined || finalData.tpPraktikumMap[tp.id] !== undefined;
        });
      });

      const sheetName = `Rapor Kelas ${cls}`.substring(0, 31);
      const worksheet = workbook.addWorksheet(sheetName);
      worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

      const totalCols = 7 + 2 * classTPsWithGrades.length;
      
      const getColumnLetter = (colIndex: number) => {
        let letter = '';
        while (colIndex > 0) {
          let remainder = (colIndex - 1) % 26;
          letter = String.fromCharCode(65 + remainder) + letter;
          colIndex = Math.floor((colIndex - 1) / 26);
        }
        return letter;
      };
      const lastColLetter = getColumnLetter(totalCols);

      if (schoolSettings.departmentLogoUrl) {
        await addImageToWorksheet(workbook, worksheet, schoolSettings.departmentLogoUrl, 0.2, 0.2);
      }
      if (schoolSettings.logoUrl) {
        await addImageToWorksheet(workbook, worksheet, schoolSettings.logoUrl, totalCols - 1.5, 0.2);
      }

      worksheet.mergeCells(`A1:${lastColLetter}1`);
      worksheet.getCell('A1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
      worksheet.getCell('A1').font = { bold: true, size: 12 };
      worksheet.getCell('A1').alignment = { horizontal: 'center' };

      worksheet.mergeCells(`A2:${lastColLetter}2`);
      worksheet.getCell('A2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
      worksheet.getCell('A2').font = { bold: true, size: 12 };
      worksheet.getCell('A2').alignment = { horizontal: 'center' };

      worksheet.mergeCells(`A3:${lastColLetter}3`);
      worksheet.getCell('A3').value = schoolSettings.schoolName.toUpperCase();
      worksheet.getCell('A3').font = { bold: true, size: 14 };
      worksheet.getCell('A3').alignment = { horizontal: 'center' };

      worksheet.mergeCells(`A4:${lastColLetter}4`);
      worksheet.getCell('A4').value = schoolSettings.address;
      worksheet.getCell('A4').font = { size: 10 };
      worksheet.getCell('A4').alignment = { horizontal: 'center' };

      let currentRow = 5;
      if (schoolSettings.additionalKopLine) {
        worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
        worksheet.getCell(`A${currentRow}`).value = schoolSettings.additionalKopLine;
        worksheet.getCell(`A${currentRow}`).font = { size: 9 };
        worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
        currentRow++;
      }

      currentRow++; // Empty row
      
      worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = 'REKAPITULASI NILAI RAPOR AKHIR SISWA';
      worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
      currentRow++;

      worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = `KELAS: ${cls} | MATA PELAJARAN: ${activeUser.subject.toUpperCase()}`;
      worksheet.getCell(`A${currentRow}`).font = { bold: true };
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
      currentRow++;
      
      worksheet.addRow([]);
      worksheet.addRow(['', 'Nama Guru', ':', activeUser.name, '', 'Semester', ':', schoolSettings?.semester || '2 (Genap)']);
      worksheet.addRow(['', '', '', '', '', 'Tahun Pelajaran', ':', schoolSettings?.academicYear || '2025/2026']);
      worksheet.addRow(['', 'Bobot Nilai', ':', `S:${weights.sumatif}% | P:${weights.praktikum}% | S.Akhir:${weights.sas}%`]);
      worksheet.addRow([]);

      const headerRow = worksheet.addRow([
        'NO',
        'NAMA SISWA',
        'NISN',
        ...classTPsWithGrades.map(tp => `S-${tp.code}`),
        ...classTPsWithGrades.map(tp => `P-${tp.code}`),
        'RATA SUMATIF',
        'RATA PRAKTIKUM',
        'Sumatif Akhir',
        'NILAI AKHIR',
        'NILAI RAPOR'
      ]);
      headerRow.eachCell((cell) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
          cell.font = { bold: true };
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });

      studentsInClass.forEach((student, idx) => {
          const finalData = calculateStudentFinal(student.id, cls);
          const dataRow = worksheet.addRow([
              idx + 1,
              student.name.toUpperCase(),
              student.nisn || student.nis || '',
              ...classTPsWithGrades.map(tp => finalData.tpSumatifMap[tp.id] !== undefined ? Math.round(finalData.tpSumatifMap[tp.id]) : '-'),
              ...classTPsWithGrades.map(tp => finalData.tpPraktikumMap[tp.id] !== undefined ? Math.round(finalData.tpPraktikumMap[tp.id]) : '-'),
              finalData.sumatif,
              finalData.praktikum,
              finalData.sas,
              finalData.final,
              finalData.roundedFinal
          ]);
          dataRow.eachCell((cell, colNumber) => {
              cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
              if (colNumber !== 2) {
                  cell.alignment = { horizontal: 'center' };
              }
          });
      });

      const todayStr = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
      worksheet.addRow([]);
      worksheet.addRow([]);
      
      const totalColsForSig = 7 + 2 * classTPsWithGrades.length;
      const createSigRow = (leftText: string, rightText: string) => {
        const row = new Array(totalColsForSig).fill('');
        row[1] = leftText;
        row[totalColsForSig - 2] = rightText;
        return row;
      };

      worksheet.addRow(createSigRow('', `Kawali, ${todayStr}`));
      worksheet.addRow(createSigRow('', 'Guru Mata Pelajaran,'));
      worksheet.addRow(createSigRow('', ''));
      worksheet.addRow([]);
      worksheet.addRow([]);
      worksheet.addRow([]);
      worksheet.addRow(createSigRow('', activeUser.name));
      worksheet.addRow(createSigRow('', ''));

      worksheet.columns = [
          { width: 5 },
          { width: 35 },
          { width: 15 },
          ...classTPsWithGrades.map(() => ({ width: 10 })),
          ...classTPsWithGrades.map(() => ({ width: 10 })),
          { width: 12 },
          { width: 12 },
          { width: 15 },
          { width: 15 },
          { width: 15 }
      ];
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const FileSaver = await import('file-saver');
    FileSaver.saveAs(blob, `Nilai_Rapor_Semua_Kelas_${activeUser.subject}.xlsx`);
  };

  const handleExportClassFinal = async () => {
    if (!selectedClass) return;
    const studentsInClass = (students || [])
      .filter(s => s.class === selectedClass && s.status === 'Aktif')
      .sort((a, b) => a.name.localeCompare(b.name));

    // Exclude TPs that have neither summative nor practical scores for all students in the class
    const classTPsWithGrades = uniqueTPs.filter(tp => {
      return studentsInClass.some(student => {
        const finalData = calculateStudentFinal(student.id, selectedClass);
        return finalData.tpSumatifMap[tp.id] !== undefined || finalData.tpPraktikumMap[tp.id] !== undefined;
      });
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Nilai Rapor ${selectedClass}`);
    worksheet.pageSetup = { paperSize: 9, orientation: 'portrait' };

    const totalCols = 7 + 2 * classTPsWithGrades.length;
    // Helper function to get Excel column letter (e.g., 1 -> A, 27 -> AA)
    const getColumnLetter = (colIndex: number) => {
      let letter = '';
      while (colIndex > 0) {
        let remainder = (colIndex - 1) % 26;
        letter = String.fromCharCode(65 + remainder) + letter;
        colIndex = Math.floor((colIndex - 1) / 26);
      }
      return letter;
    };
    const lastColLetter = getColumnLetter(totalCols);

    // Add Logos
    if (schoolSettings.departmentLogoUrl) {
      await addImageToWorksheet(workbook, worksheet, schoolSettings.departmentLogoUrl, 0.2, 0.2);
    }
    if (schoolSettings.logoUrl) {
      await addImageToWorksheet(workbook, worksheet, schoolSettings.logoUrl, totalCols - 1.5, 0.2);
    }

    // Add Header (Kop)
    worksheet.mergeCells(`A1:${lastColLetter}1`);
    worksheet.getCell('A1').value = schoolSettings.governmentName || 'PEMERINTAH KABUPATEN CIAMIS';
    worksheet.getCell('A1').font = { bold: true, size: 12 };
    worksheet.getCell('A1').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A2:${lastColLetter}2`);
    worksheet.getCell('A2').value = schoolSettings.departmentName || 'DINAS PENDIDIKAN';
    worksheet.getCell('A2').font = { bold: true, size: 12 };
    worksheet.getCell('A2').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A3:${lastColLetter}3`);
    worksheet.getCell('A3').value = schoolSettings.schoolName.toUpperCase();
    worksheet.getCell('A3').font = { bold: true, size: 14 };
    worksheet.getCell('A3').alignment = { horizontal: 'center' };

    worksheet.mergeCells(`A4:${lastColLetter}4`);
    worksheet.getCell('A4').value = schoolSettings.address;
    worksheet.getCell('A4').font = { size: 10 };
    worksheet.getCell('A4').alignment = { horizontal: 'center' };

    let currentRow = 5;
    if (schoolSettings.additionalKopLine) {
      worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
      worksheet.getCell(`A${currentRow}`).value = schoolSettings.additionalKopLine;
      worksheet.getCell(`A${currentRow}`).font = { size: 9 };
      worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
      currentRow++;
    }

    currentRow++; // Empty row
    
    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = 'REKAPITULASI NILAI RAPOR AKHIR SISWA';
    worksheet.getCell(`A${currentRow}`).font = { bold: true, size: 12 };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;

    worksheet.mergeCells(`A${currentRow}:${lastColLetter}${currentRow}`);
    worksheet.getCell(`A${currentRow}`).value = `KELAS: ${selectedClass} | MATA PELAJARAN: ${activeUser.subject.toUpperCase()}`;
    worksheet.getCell(`A${currentRow}`).font = { bold: true };
    worksheet.getCell(`A${currentRow}`).alignment = { horizontal: 'center' };
    currentRow++;
    
    worksheet.addRow([]);
    worksheet.addRow(['', 'Nama Guru', ':', activeUser.name, '', 'Semester', ':', schoolSettings?.semester || '2 (Genap)']);
    worksheet.addRow(['', '', '', '', '', 'Tahun Pelajaran', ':', schoolSettings?.academicYear || '2025/2026']);
    worksheet.addRow(['', 'Bobot Nilai', ':', `S:${weights.sumatif}% | P:${weights.praktikum}% | S.Akhir:${weights.sas}%`]);
    worksheet.addRow([]);

    // Add Table Header
    const headerRow = worksheet.addRow([
      'NO',
      'NAMA SISWA',
      'NISN',
      ...classTPsWithGrades.map(tp => `S-${tp.code}`),
      ...classTPsWithGrades.map(tp => `P-${tp.code}`),
      'RATA SUMATIF',
      'RATA PRAKTIKUM',
      'Sumatif Akhir',
      'NILAI AKHIR',
      'NILAI RAPOR'
    ]);
    headerRow.eachCell((cell) => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };
        cell.font = { bold: true };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    // Add Table Data
    studentsInClass.forEach((student, idx) => {
        const finalData = calculateStudentFinal(student.id, selectedClass);
        const dataRow = worksheet.addRow([
            idx + 1,
            student.name.toUpperCase(),
            student.nisn || student.nis || '',
            ...classTPsWithGrades.map(tp => finalData.tpSumatifMap[tp.id] !== undefined ? Math.round(finalData.tpSumatifMap[tp.id]) : '-'),
            ...classTPsWithGrades.map(tp => finalData.tpPraktikumMap[tp.id] !== undefined ? Math.round(finalData.tpPraktikumMap[tp.id]) : '-'),
            finalData.sumatif,
            finalData.praktikum,
            finalData.sas,
            finalData.final,
            finalData.roundedFinal
        ]);
        dataRow.eachCell((cell, colNumber) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            if (colNumber !== 2) { // Center everything except name
                cell.alignment = { horizontal: 'center' };
            }
        });
    });

    // Add Signature
    const today = new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' });
    worksheet.addRow([]);
    worksheet.addRow([]);
    
    const totalColsForSig = 7 + 2 * classTPsWithGrades.length;
    const createSigRow = (leftText: string, rightText: string) => {
      const row = new Array(totalColsForSig).fill('');
      row[1] = leftText; // Column B
      row[totalColsForSig - 2] = rightText; // Second to last column
      return row;
    };

    worksheet.addRow(createSigRow('', `Kawali, ${today}`));
    worksheet.addRow(createSigRow('', 'Guru Mata Pelajaran,'));
    worksheet.addRow(createSigRow('', ''));
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow([]);
    worksheet.addRow(createSigRow('', activeUser.name));
    worksheet.addRow(createSigRow('', ''));

    // Set column widths
    worksheet.columns = [
        { width: 5 },  // NO
        { width: 35 }, // NAMA SISWA
        { width: 15 }, // NISN
        ...classTPsWithGrades.map(() => ({ width: 10 })), // S-TP columns
        ...classTPsWithGrades.map(() => ({ width: 10 })), // P-TP columns
        { width: 12 }, // RATA SUMATIF
        { width: 12 }, // RATA PRAKTIKUM
        { width: 15 }, // Sumatif Akhir
        { width: 15 }, // NILAI AKHIR
        { width: 15 }  // NILAI RAPOR
    ];

    // Save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    import('file-saver').then(FileSaver => {
        FileSaver.saveAs(blob, `Nilai_Rapor_${selectedClass}_${activeUser.subject}.xlsx`);
    });
  };

  const getLevelColor = (level?: FormativeLevel | string) => {
    switch (level) {
      case 'Awal Berkembang': return 'bg-rose-100 text-rose-700';
      case 'Layak': return 'bg-orange-100 text-orange-700';
      case 'Mahir':
      case 'Cakap': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-slate-100 text-slate-400';
    }
  };


  return (
    <div className="space-y-8 animate-fade-in pb-20">
      <div className="flex bg-white dark:bg-slate-900 p-2 rounded-[1.5rem] border border-slate-200 dark:border-slate-800 w-fit shadow-sm flex-wrap">
        <button onClick={() => { setActiveTab('akhir'); setSelectedClass(null); }} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'akhir' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600'}`}><TableIcon size={16} /> Nilai Akhir Kelas</button>
        <button onClick={() => setActiveTab('rekap')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'rekap' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600'}`}><BarChart3 size={16} /> Rekap Rombel</button>
        <button onClick={() => setActiveTab('rekapFormatif')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'rekapFormatif' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600'}`}><LayoutGrid size={16} /> Rekap Formatif</button>
        <button onClick={() => setActiveTab('rangking')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${activeTab === 'rangking' ? `bg-gradient-to-r ${currentTheme.activeNavGradient || currentTheme.accentGradient} text-white shadow-lg shadow-black/10` : 'text-slate-400 hover:text-slate-600'}`}><Users size={16} /> Rekap Rangking Pararel</button>
      </div>

      {activeTab === 'akhir' ? (
        !selectedClass ? (
          <div className="space-y-8">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Nilai Rapor Akhir</h2>
                <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">S.Akhir Visibility</span>
                  <button 
                    onClick={() => onUpdateTeacher && onUpdateTeacher({ ...activeUser, SasVisibility: activeUser.SasVisibility === undefined ? false : !activeUser.SasVisibility })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${activeUser.SasVisibility !== false ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${activeUser.SasVisibility !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  {activeUser.SasVisibility !== false ? <Eye size={14} className="text-indigo-600" /> : <EyeOff size={14} className="text-slate-400" />}
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Visibilitas Nilai Rapor Akhir</span>
                  <button 
                    onClick={() => onUpdateTeacher && onUpdateTeacher({ ...activeUser, RaporVisibility: activeUser.RaporVisibility === undefined ? false : !activeUser.RaporVisibility })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${activeUser.RaporVisibility !== false ? 'bg-rose-600' : 'bg-slate-200 dark:bg-slate-700'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${activeUser.RaporVisibility !== false ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                  {activeUser.RaporVisibility !== false ? <Eye size={14} className="text-rose-600" /> : <EyeOff size={14} className="text-slate-400" />}
                </div>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Tentukan bobot penilaian dan pilih kelas untuk melihat rekapitulasi.</p>
            </div>
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5 text-indigo-600 pointer-events-none"><Settings2 size={120} /></div>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-500/20"><Percent size={20} /></div>
                    <div>
                      <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight leading-none">Pengaturan Bobot Nilai</h3>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">Berlaku untuk seluruh rombel yang Anda ajar</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <button 
                      onClick={handleSaveWeights}
                      disabled={isSavingWeights || weights.sumatif + weights.praktikum + weights.sas !== 100}
                      className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all ${
                        weights.sumatif + weights.praktikum + weights.sas === 100 
                          ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 active:scale-95' 
                          : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {isSavingWeights ? 'Menyimpan...' : 'Simpan Bobot'}
                    </button>
                    <button 
                      onClick={handlePrintAllClasses}
                      className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-black rounded-2xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-all text-xs tracking-widest uppercase border border-slate-200 dark:border-slate-700 shadow-sm"
                    >
                      <Printer size={16} /> Cetak Semua Kelas
                    </button>
                    <button 
                      onClick={handleExportAllClasses}
                      className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-black rounded-2xl hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 active:scale-95 transition-all text-xs tracking-widest uppercase"
                    >
                      <FileSpreadsheet size={16} /> Export Excel Semua Kelas
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl relative z-10">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Sumatif TP (%)</label>
                    <div className="relative">
                      <input type="number" value={weights.sumatif} onChange={e => handleWeightChange('sumatif', e.target.value)} className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-black text-indigo-600 dark:text-indigo-400 focus:ring-4 focus:ring-indigo-500/10 transition-all text-xl" />
                      <span className="absolute right-5 top-1/2 -translate-y-1/2 font-black text-slate-300">%</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Praktikum TP (%)</label>
                    <div className="relative">
                      <input type="number" value={weights.praktikum} onChange={e => handleWeightChange('praktikum', e.target.value)} className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-black text-indigo-600 dark:text-indigo-400 focus:ring-4 focus:ring-indigo-500/10 transition-all text-xl" />
                      <span className="absolute right-5 top-1/2 -translate-y-1/2 font-black text-slate-300">%</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Sumatif Akhir (%)</label>
                    <div className="relative">
                      <input type="number" value={weights.sas} onChange={e => handleWeightChange('sas', e.target.value)} className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl outline-none font-black text-indigo-600 dark:text-indigo-400 focus:ring-4 focus:ring-indigo-500/10 transition-all text-xl" />
                      <span className="absolute right-5 top-1/2 -translate-y-1/2 font-black text-slate-300">%</span>
                    </div>
                  </div>
                </div>
                <div className={`mt-8 inline-flex items-center gap-3 px-5 py-2.5 rounded-2xl text-[11px] font-black uppercase tracking-widest ${weights.sumatif + weights.praktikum + weights.sas === 100 ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700 animate-pulse'}`}>
                  <Info size={16} /> Total: {weights.sumatif + weights.praktikum + weights.sas}% {weights.sumatif + weights.praktikum + weights.sas !== 100 ? ' (HARUS 100%)' : ' (BOBOT VALID)'}
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {myClasses.map((cls, index) => {
                const colors = [
                  'bg-indigo-500', 'bg-rose-500', 'bg-emerald-500', 'bg-amber-500', 
                  'bg-blue-500', 'bg-purple-500', 'bg-teal-500', 'bg-orange-500'
                ];
                const color = colors[index % colors.length];
                return (
                  <button 
                    key={cls} 
                    onClick={() => setSelectedClass(cls)} 
                    className={`group p-8 bg-white dark:bg-slate-900 border-2 ${color.replace('bg-', 'border-')} rounded-[2.5rem] hover:shadow-2xl hover:shadow-${color.replace('bg-', '')}-500/20 transition-all flex flex-col items-center gap-4 relative overflow-hidden`}
                  >
                    <div className={`absolute top-0 right-0 w-24 h-24 ${color} opacity-[0.05] rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform`}></div>
                    <div className={`w-16 h-16 ${color} text-white rounded-2xl flex items-center justify-center font-black text-2xl shadow-sm transition-all`}>
                      {cls}
                    </div>
                    <div className="text-center">
                      <p className="font-black text-slate-800 dark:text-white uppercase tracking-widest text-xs mb-1">KELAS {cls}</p>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">
                        {(students || []).filter(s => s.class === cls && s.status === 'Aktif').length} SISWA AKTIF
                      </p>
                    </div>
                    <div className={`mt-2 text-slate-400 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all`}>
                      <ArrowRight size={20} />
                    </div>
                  </button>
                );
              })}
              {myClasses.length === 0 && (
                <div className="col-span-full py-20 text-center bg-slate-50/50 dark:bg-slate-800/10 rounded-[3rem] border-2 border-dashed border-slate-200 dark:border-slate-800">
                  <Users className="mx-auto mb-4 text-slate-200" size={64} />
                  <p className="text-slate-400 font-bold italic">Anda belum memiliki kelas yang diampu.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-8 animate-fade-in-up">
            <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
  <div className="flex items-center gap-4">
    <button onClick={() => setSelectedClass(null)} className="p-3 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all shadow-sm border border-slate-200 dark:border-slate-800" title="Kembali ke Daftar Kelas">
      <ChevronLeft size={20} />
    </button>
    <div className="flex items-center gap-2">
      <button 
        onClick={() => {
          const currentIndex = myClasses.indexOf(selectedClass!);
          if (currentIndex > 0) setSelectedClass(myClasses[currentIndex - 1]);
        }}
        disabled={myClasses.indexOf(selectedClass!) <= 0}
        className="p-2 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-xl hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm border border-slate-200 dark:border-slate-800"
        title="Kelas Sebelumnya"
      >
        <ChevronLeft size={16} />
      </button>
      <button 
        onClick={() => {
          const currentIndex = myClasses.indexOf(selectedClass!);
          if (currentIndex < myClasses.length - 1) setSelectedClass(myClasses[currentIndex + 1]);
        }}
        disabled={myClasses.indexOf(selectedClass!) >= myClasses.length - 1}
        className="p-2 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 rounded-xl hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm border border-slate-200 dark:border-slate-800"
        title="Kelas Berikutnya"
      >
        <ChevronRight size={16} />
      </button>
    </div>
    <div>
      <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight leading-none mb-1">Rekapitulasi Kelas {selectedClass}</h2>
      <div className="flex items-center gap-3">
        <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">Mata Pelajaran: {activeUser.subject}</p>
      </div>
    </div>
  </div>
  <div className="flex items-center gap-3">
    <button onClick={handlePrintClassFinal} className="flex items-center gap-2 px-6 py-3.5 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 font-black rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all text-xs tracking-widest uppercase border border-slate-200 dark:border-slate-800">
      <Printer size={18} /> Print
    </button>
    <button onClick={handleExportClassFinal} className="flex items-center gap-2 px-8 py-3.5 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 shadow-xl shadow-indigo-500/20 active:scale-95 transition-all text-xs tracking-widest uppercase">
      <Download size={18} /> Export Excel
    </button>
  </div>
</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 rounded-2xl"><Users size={24} /></div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Total Siswa</p>
                  <p className="text-xl font-black text-slate-800 dark:text-white">{(students || []).filter(s => s.class === selectedClass && s.status === 'Aktif').length}</p>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 rounded-2xl"><BarChart3 size={24} /></div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Rata-rata Kelas</p>
                  <p className="text-xl font-black text-slate-800 dark:text-white">{classAnalysis.classAverage}</p>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 rounded-2xl"><CheckCircle size={24} /></div>
                <div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Mencapai Target (&ge; {batasCapaian})</p>
                  <p className="text-xl font-black text-slate-800 dark:text-white">{classAnalysis.targetAchievementCount} / {classAnalysis.studentCount}</p>
                </div>
              </div>
              <div className="bg-white dark:bg-slate-900 p-6 rounded-[2rem] border border-slate-200 dark:border-slate-800 shadow-sm flex items-center gap-4">
                <div className="p-3 bg-amber-100 dark:bg-amber-500/20 text-amber-600 rounded-2xl"><GraduationCap size={24} /></div>
                <div>
                  <label htmlFor="batasCapaian" className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1 block">Batas Capaian</label>
                  <input 
                    id="batasCapaian"
                    type="number" 
                    value={batasCapaian} 
                    onChange={(e) => setBatasCapaian(Number(e.target.value))}
                    className="text-xl font-black text-slate-800 dark:text-white bg-transparent border-b-2 border-slate-200 dark:border-slate-700 w-16 outline-none focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-8 sm:p-10 rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-cyan-100 dark:bg-cyan-500/20 text-cyan-600 rounded-xl"><TableIcon size={20} /></div>
                  <div>
                    <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">Tabel Hasil Akhir</h3>
                    <p className="text-[10px] text-slate-400 font-medium">Rekapitulasi Nilai Rapor & Sumatif Akhir per Siswa</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                  {onUpdateSasGrades && (
                    <button
                      onClick={handleSaveFinalGradesToFirebase}
                      disabled={isSavingFinalGrades || !selectedClass}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-2xl font-bold text-xs shadow-md shadow-emerald-500/20 hover:shadow-lg hover:shadow-emerald-500/30 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CheckCircle size={15} />
                      {isSavingFinalGrades ? 'Menyimpan ke Firebase...' : 'Simpan Nilai Rapor Ke Firebase'}
                    </button>
                  )}
                </div>
              </div>

              {saveSuccessMsg && (
                <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 rounded-2xl text-emerald-800 dark:text-emerald-300 text-xs font-bold flex items-center gap-3 shadow-sm animate-fade-in">
                  <CheckCircle size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>{saveSuccessMsg}</span>
                </div>
              )}
              
              {/* Desktop Mode Table */}
              <div className="hidden md:block overflow-x-auto overflow-y-clip">
                <table className="w-full text-left bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                    <tr className="uppercase text-[8px] lg:text-[10px] font-bold tracking-tight text-slate-500 dark:text-slate-400">
                      <th className="px-1.5 py-3 text-center w-8">No</th>
                      <th onClick={() => handleSort('name')} className="px-2 py-3 text-left cursor-pointer hover:text-indigo-600 transition-colors group min-w-[140px]">
                        <div className="flex items-center gap-1">
                          Nama Siswa <ArrowUpDown size={10} className={sortConfig.key === 'name' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} />
                        </div>
                      </th>
                      {(uniqueTPs || []).filter(tp => tp.isSummative).map(tp => (
                        <th key={`s_${tp.id}`} className="px-1 py-3 text-center border-l border-slate-100 dark:border-slate-800/50" title={`Sumatif ${tp.description}`}>
                          S-{tp.code}
                        </th>
                      ))}
                      {(uniqueTPs || []).filter(tp => tp.isPracticum).map(tp => (
                        <th key={`p_${tp.id}`} className="px-1 py-3 text-center border-l border-slate-100 dark:border-slate-800/50" title={`Praktikum ${tp.description}`}>
                          P-{tp.code}
                        </th>
                      ))}
                      <th onClick={() => handleSort('sumatif')} className="px-1.5 py-3 text-center border-l border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group">
                        <div className="flex items-center justify-center gap-0.5">
                          Rata S. <ArrowUpDown size={10} className={sortConfig.key === 'sumatif' ? 'opacity-100 text-blue-600' : 'opacity-0 group-hover:opacity-100'} />
                        </div>
                      </th>
                      <th onClick={() => handleSort('praktikum')} className="px-1.5 py-3 text-center border-l border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group">
                        <div className="flex items-center justify-center gap-0.5">
                          Rata P. <ArrowUpDown size={10} className={sortConfig.key === 'praktikum' ? 'opacity-100 text-teal-600' : 'opacity-0 group-hover:opacity-100'} />
                        </div>
                      </th>
                      <th onClick={() => handleSort('sas')} className="px-1.5 py-3 text-center border-l border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group">
                        <div className="flex flex-col items-center justify-center leading-none">
                          <span className="mb-0.5">S. Akhir</span>
                          <div className="flex items-center justify-center gap-0.5">
                            <span className="text-[7px] lg:text-[8px] opacity-75 font-normal lowercase italic">(evaluasi)</span>
                            <ArrowUpDown size={10} className={sortConfig.key === 'sas' ? 'opacity-100 text-amber-600' : 'opacity-0 group-hover:opacity-100'} />
                          </div>
                        </div>
                      </th>
                      <th onClick={() => handleSort('total')} className="px-1.5 py-3 text-center border-l border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group">
                        <div className="flex items-center justify-center gap-0.5">
                          Jml <ArrowUpDown size={10} className={sortConfig.key === 'total' ? 'opacity-100 text-indigo-600' : 'opacity-0 group-hover:opacity-100'} />
                        </div>
                      </th>
                      <th onClick={() => handleSort('final')} className="px-1.5 py-3 text-center border-l border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group">
                        <div className="flex flex-col items-center justify-center leading-none">
                          <span className="mb-0.5">Nilai</span>
                          <div className="flex items-center justify-center gap-0.5">
                            <span>Akhir</span>
                            <ArrowUpDown size={10} className={sortConfig.key === 'final' ? 'opacity-100 text-rose-600' : 'opacity-0 group-hover:opacity-100'} />
                          </div>
                        </div>
                      </th>
                      <th onClick={() => handleSort('alpa')} className="px-1.5 py-3 text-center border-l border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group">
                        <div className="flex flex-col items-center justify-center leading-none">
                          <span className="mb-0.5">Jumlah</span>
                          <div className="flex items-center justify-center gap-0.5">
                            <span>Alpa</span>
                            <ArrowUpDown size={10} className={sortConfig.key === 'alpa' ? 'opacity-100 text-rose-500' : 'opacity-0 group-hover:opacity-100'} />
                          </div>
                        </div>
                      </th>
                      <th onClick={() => handleSort('roundedFinal')} className="px-1.5 py-3 text-center border-l border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group">
                        <div className="flex flex-col items-center justify-center leading-none">
                          <span className="mb-0.5">Nilai</span>
                          <div className="flex items-center justify-center gap-0.5">
                            <span>Rapor</span>
                            <ArrowUpDown size={10} className={sortConfig.key === 'roundedFinal' ? 'opacity-100 text-slate-800' : 'opacity-0 group-hover:opacity-100'} />
                          </div>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {sortedStudents.map((student, idx) => {
                      const finalData = calculateStudentFinal(student.id, selectedClass || '');
                      const alpaCount = attendanceRecords?.filter(r => r.studentId === student.id && r.class === (selectedClass || '') && r.status === 'Alpa').length || 0;
                      const sakitCount = attendanceRecords?.filter(r => r.studentId === student.id && r.class === (selectedClass || '') && r.status === 'Sakit').length || 0;
                      const izinCount = attendanceRecords?.filter(r => r.studentId === student.id && r.class === (selectedClass || '') && r.status === 'Izin').length || 0;

                      return (
                        <tr id={`student-row-${student.id}`} key={student.id} className="text-[11px] lg:text-xs hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                          <td className="px-1.5 py-2.5 text-center text-slate-500 font-mono">{idx + 1}</td>
                          <td className={`px-2 py-2.5 font-medium transition-colors cursor-pointer hover:underline ${student.gender === 'Perempuan' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-800 dark:text-slate-200'}`} onClick={() => onStudentRecapClick && onStudentRecapClick(student)}>
                            {student.name.toUpperCase()}
                            {(alpaCount > 0 || sakitCount > 0 || izinCount > 0) && (
                              <span className="ml-1.5 inline-flex gap-0.5 text-[8px] font-bold">
                                {alpaCount > 0 && <span className="text-rose-500 bg-rose-50 border border-rose-100 dark:bg-rose-500/10 px-0.5 py-0.2 rounded">A:{alpaCount}</span>}
                                {sakitCount > 0 && <span className="text-amber-500 bg-amber-50 border border-amber-100 dark:bg-amber-500/10 px-0.5 py-0.2 rounded">S:{sakitCount}</span>}
                                {izinCount > 0 && <span className="text-blue-500 bg-blue-50 border border-blue-100 dark:bg-blue-500/10 px-0.5 py-0.2 rounded">I:{izinCount}</span>}
                              </span>
                            )}
                          </td>
                          {(uniqueTPs || []).filter(tp => tp.isSummative).map(tp => (
                            <td key={`s_${tp.id}`} className="px-1 py-2 text-center border-l border-slate-100 dark:border-slate-800/50">
                              <EditableScore initialValue={finalData.tpSumatifMap[tp.id] !== undefined ? Math.round(finalData.tpSumatifMap[tp.id]) : null} studentId={student.id} tpId={tp.id} activityType={ActivityType.SUMATIF} />
                            </td>
                          ))}
                          {(uniqueTPs || []).filter(tp => tp.isPracticum).map(tp => (
                            <td key={`p_${tp.id}`} className="px-1 py-2 text-center border-l border-slate-100 dark:border-slate-800/50">
                              <EditableScore initialValue={finalData.tpPraktikumMap[tp.id] !== undefined ? Math.round(finalData.tpPraktikumMap[tp.id]) : null} studentId={student.id} tpId={tp.id} activityType={ActivityType.PRAKTIKUM} />
                            </td>
                          ))}
                          <td className="px-1.5 py-2.5 text-center font-black text-blue-600 text-xs lg:text-sm font-mono border-l border-slate-100 dark:border-slate-800/50">{finalData.sumatif}</td>
                          <td className="px-1.5 py-2.5 text-center font-black text-teal-600 text-xs lg:text-sm font-mono border-l border-slate-100 dark:border-slate-800/50">{finalData.praktikum}</td>
                          <td className="px-1.5 py-2.5 text-center font-black text-amber-600 text-xs lg:text-sm font-mono border-l border-slate-100 dark:border-slate-800/50">
                            <SasInput 
                              initialValue={typeof finalData.sas === 'number' ? finalData.sas : 0} 
                              onSave={(val) => handleUpdateSasGrade(student.id, val)} 
                            />
                          </td>
                          <td className="px-1.5 py-2.5 text-center font-black text-indigo-600 text-xs lg:text-sm font-mono border-l border-slate-100 dark:border-slate-800/50">{finalData.total}</td>
                          <td className="px-1.5 py-2.5 text-center font-black text-rose-600 text-xs lg:text-sm font-mono border-l border-slate-100 dark:border-slate-800/50">{finalData.final}</td>
                          <td className="px-1.5 py-2.5 text-center font-black text-rose-500 text-xs lg:text-sm font-mono border-l border-slate-100 dark:border-slate-800/50">{alpaCount}</td>
                          <td className="px-1.5 py-2.5 text-center bg-slate-50 dark:bg-slate-800/50 font-black font-mono text-sm lg:text-base border-l border-slate-100 dark:border-slate-800/50">{finalData.roundedFinal}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Mode Layout (Card List) */}
              <div className="block md:hidden space-y-6">
                {/* Mobile Sort Dropdown */}
                <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-800/40 p-4 rounded-2xl border border-slate-100 dark:border-slate-800/80 mb-4">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Urutkan:</span>
                  <select
                    value={`${sortConfig.key}-${sortConfig.direction}`}
                    onChange={(e) => {
                      const [key, dir] = e.target.value.split('-') as [any, any];
                      setSortConfig({ key, direction: dir });
                    }}
                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-xs font-bold px-3 py-2 rounded-xl text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-indigo-500/20"
                  >
                    <option value="name-asc">Nama (A - Z)</option>
                    <option value="name-desc">Nama (Z - A)</option>
                    <option value="sumatif-desc">Rata Sumatif Tertinggi</option>
                    <option value="sumatif-asc">Rata Sumatif Terendah</option>
                    <option value="praktikum-desc">Rata Praktikum Tertinggi</option>
                    <option value="praktikum-asc">Rata Praktikum Terendah</option>
                    <option value="sas-desc">Sumatif Akhir Tertinggi</option>
                    <option value="sas-asc">Sumatif Akhir Terendah</option>
                    <option value="final-desc">Nilai Akhir Tertinggi</option>
                    <option value="final-asc">Nilai Akhir Terendah</option>
                    <option value="alpa-desc">Alpa Terbanyak</option>
                    <option value="alpa-asc">Alpa Tersedikit</option>
                    <option value="roundedFinal-desc">Nilai Rapor Tertinggi</option>
                    <option value="roundedFinal-asc">Nilai Rapor Terendah</option>
                  </select>
                </div>

                {sortedStudents.map((student, idx) => {
                  const finalData = calculateStudentFinal(student.id, selectedClass || '');
                  const alpaCount = attendanceRecords?.filter(r => r.studentId === student.id && r.class === (selectedClass || '') && r.status === 'Alpa').length || 0;
                  const sakitCount = attendanceRecords?.filter(r => r.studentId === student.id && r.class === (selectedClass || '') && r.status === 'Sakit').length || 0;
                  const izinCount = attendanceRecords?.filter(r => r.studentId === student.id && r.class === (selectedClass || '') && r.status === 'Izin').length || 0;
                  const isExpanded = !!expandedStudents[student.id];

                  const isPassed = finalData.roundedFinal >= batasCapaian;

                  return (
                    <div 
                      key={`mobile-card-${student.id}`} 
                      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-5 shadow-sm transition-all relative overflow-hidden"
                    >
                      {/* Gender-based side accent line */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${student.gender === 'Perempuan' ? 'bg-rose-500' : 'bg-blue-500'}`} />
                      
                      {/* Card Header */}
                      <div className="flex items-start justify-between gap-3 pl-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                            <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
                              #{idx + 1}
                            </span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider ${student.gender === 'Perempuan' ? 'text-rose-600 bg-rose-50 dark:bg-rose-500/10' : 'text-blue-600 bg-blue-50 dark:bg-blue-500/10'}`}>
                              {student.gender === 'Perempuan' ? 'P' : 'L'}
                            </span>
                            {(alpaCount > 0 || sakitCount > 0 || izinCount > 0) && (
                              <div className="flex gap-1 text-[9px] font-bold">
                                {alpaCount > 0 && <span className="text-rose-500 bg-rose-50 border border-rose-100 dark:bg-rose-500/10 px-1 py-0.5 rounded">A:{alpaCount}</span>}
                                {sakitCount > 0 && <span className="text-amber-500 bg-amber-50 border border-amber-100 dark:bg-amber-500/10 px-1 py-0.5 rounded">S:{sakitCount}</span>}
                                {izinCount > 0 && <span className="text-blue-500 bg-blue-50 border border-blue-100 dark:bg-blue-500/10 px-1 py-0.5 rounded">I:{izinCount}</span>}
                              </div>
                            )}
                          </div>
                          
                          <h4 
                            className="font-black text-slate-800 dark:text-white text-sm hover:underline cursor-pointer uppercase leading-tight"
                            onClick={() => onStudentRecapClick && onStudentRecapClick(student)}
                          >
                            {student.name}
                          </h4>
                        </div>

                        {/* Nilai Rapor Badge */}
                        <div className="text-center">
                          <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center border ${
                            isPassed 
                              ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400' 
                              : 'bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20 text-rose-600 dark:text-rose-400'
                          }`}>
                            <span className="text-[9px] font-black uppercase tracking-wider leading-none opacity-60 mb-0.5">Rapor</span>
                            <span className="text-lg font-black font-mono leading-none">{finalData.roundedFinal}</span>
                          </div>
                        </div>
                      </div>

                      {/* Summary Grid */}
                      <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 pl-2">
                        <div className="bg-slate-50 dark:bg-slate-800/40 p-2.5 rounded-2xl border border-slate-100/50 dark:border-slate-800/20">
                          <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Rata Sumatif (S)</div>
                          <div className="text-sm font-black text-blue-600 font-mono">{finalData.sumatif}</div>
                        </div>
                        
                        <div className="bg-slate-50 dark:bg-slate-800/40 p-2.5 rounded-2xl border border-slate-100/50 dark:border-slate-800/20">
                          <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Rata Praktikum (P)</div>
                          <div className="text-sm font-black text-teal-600 font-mono">{finalData.praktikum}</div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800/40 p-2.5 rounded-2xl border border-slate-100/50 dark:border-slate-800/20">
                          <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">S. Akhir (Evaluasi)</div>
                          <div className="text-sm font-black text-amber-600 font-mono">{finalData.sas}</div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-800/40 p-2.5 rounded-2xl border border-slate-100/50 dark:border-slate-800/20">
                          <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Nilai Akhir (Presisi)</div>
                          <div className="text-sm font-black text-indigo-600 font-mono">{finalData.final}</div>
                        </div>
                      </div>

                      {/* Expandable Section */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-4 pl-2 animate-fade-in-up">
                          {/* Title */}
                          <div className="flex items-center gap-2 pb-1">
                            <div className="w-1.5 h-3 bg-indigo-500 rounded-full" />
                            <h5 className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider">Input & Edit Nilai TP</h5>
                          </div>

                          {/* Sumatif TP list */}
                          <div className="space-y-2.5">
                            <div className="text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest bg-blue-50 dark:bg-blue-950/40 px-2 py-1 rounded-md inline-block">Nilai Sumatif (S)</div>
                            {(uniqueTPs || []).filter(tp => tp.isSummative).length === 0 ? (
                              <p className="text-[11px] text-slate-400 italic">Tidak ada TP Sumatif</p>
                            ) : (
                              <div className="grid grid-cols-1 gap-2">
                                {(uniqueTPs || []).filter(tp => tp.isSummative).map(tp => (
                                  <div key={`mob_s_${tp.id}`} className="flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-800/20 p-2 rounded-xl border border-slate-100 dark:border-slate-800/40">
                                    <div className="min-w-0 flex-1">
                                      <div className="text-[10px] font-bold text-slate-500 font-mono">S-{tp.code}</div>
                                      <div className="text-[11px] text-slate-600 dark:text-slate-300 truncate" title={tp.description}>{tp.description}</div>
                                    </div>
                                    <div className="flex-shrink-0">
                                      <EditableScore initialValue={finalData.tpSumatifMap[tp.id] !== undefined ? Math.round(finalData.tpSumatifMap[tp.id]) : null} studentId={student.id} tpId={tp.id} activityType={ActivityType.SUMATIF} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Praktikum TP list */}
                          <div className="space-y-2.5 pt-2">
                            <div className="text-[9px] font-black text-teal-600 dark:text-teal-400 uppercase tracking-widest bg-teal-50 dark:bg-teal-950/40 px-2 py-1 rounded-md inline-block">Nilai Praktikum (P)</div>
                            {(uniqueTPs || []).filter(tp => tp.isPracticum).length === 0 ? (
                              <p className="text-[11px] text-slate-400 italic">Tidak ada TP Praktikum</p>
                            ) : (
                              <div className="grid grid-cols-1 gap-2">
                                {(uniqueTPs || []).filter(tp => tp.isPracticum).map(tp => (
                                  <div key={`mob_p_${tp.id}`} className="flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-800/20 p-2 rounded-xl border border-slate-100 dark:border-slate-800/40">
                                    <div className="min-w-0 flex-1">
                                      <div className="text-[10px] font-bold text-slate-500 font-mono">P-{tp.code}</div>
                                      <div className="text-[11px] text-slate-600 dark:text-slate-300 truncate" title={tp.description}>{tp.description}</div>
                                    </div>
                                    <div className="flex-shrink-0">
                                      <EditableScore initialValue={finalData.tpPraktikumMap[tp.id] !== undefined ? Math.round(finalData.tpPraktikumMap[tp.id]) : null} studentId={student.id} tpId={tp.id} activityType={ActivityType.PRAKTIKUM} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Expand Button */}
                      <button 
                        onClick={() => setExpandedStudents(prev => ({ ...prev, [student.id]: !prev[student.id] }))}
                        className="w-full mt-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 font-black text-[10px] uppercase tracking-wider rounded-2xl border border-slate-100 dark:border-slate-800/80 transition-all flex items-center justify-center gap-1"
                      >
                        {isExpanded ? (
                          <>Sembunyikan Rincian <ChevronUp size={14} /></>
                        ) : (
                          <>Tampilkan & Edit Rincian TP <ChevronDown size={14} /></>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )
      ) : activeTab === 'rekap' ? (
        <div className="bg-white dark:bg-slate-900 p-8 rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm animate-fade-in-up">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <h3 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Rekap Global Per Rombel</h3>
                <div className="flex items-center gap-3">
                  <button onClick={handlePrintRekap} className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 font-black rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all text-xs tracking-widest uppercase border border-slate-200 dark:border-slate-800">
                      <Printer size={16} /> Cetak Rekap
                  </button>
                  <button onClick={handleExportRekap} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all text-xs tracking-widest uppercase">
                      <Download size={16} /> Export
                  </button>
                </div>
            </div>
            <div className="overflow-x-auto overflow-y-clip">
              <table className="min-w-full text-center border-separate border-spacing-y-2">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                      <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          <th className="px-4 py-4 rounded-l-2xl border-y border-l border-slate-200 dark:border-slate-800">No</th>
                          <th className="px-4 py-4 border-y border-slate-200 dark:border-slate-800">Kelas</th>
                          <th className="px-4 py-4 border-y border-slate-200 dark:border-slate-800">Jml Siswa</th>
                          <th className="px-4 py-4 border-y border-slate-200 dark:border-slate-800">Rata-rata</th>
                          <th className="px-4 py-4 border-y border-slate-200 dark:border-slate-800">Tertinggi</th>
                          <th className="px-4 py-4 border-y border-slate-200 dark:border-slate-800">Terendah</th>
                          <th className="px-4 py-4 rounded-r-2xl border-y border-r border-slate-200 dark:border-slate-800">Tuntas (${batasCapaian})</th>
                      </tr>
                  </thead>
                  <tbody className="text-xs font-bold text-slate-700 dark:text-slate-300">
                      {rekapitulasiData.map((d, i) => (
                          <tr key={d.className} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-all group">
                              <td className="px-4 py-4 border-y border-l border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 rounded-l-2xl">{i + 1}</td>
                              <td className="px-4 py-4 font-black text-indigo-600 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700">{d.className}</td>
                              <td className="px-4 py-4 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700">{d.studentCount}</td>
                              <td className="px-4 py-4 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 text-amber-600">{d.classAverage}</td>
                              <td className="px-4 py-4 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 text-emerald-600">{d.highestScore}</td>
                              <td className="px-4 py-4 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 text-rose-600">{d.lowestScore}</td>
                              <td className="px-4 py-4 border-y border-r border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 rounded-r-2xl font-black">{d.targetAchievementCount} / {d.studentCount} Siswa</td>
                          </tr>
                      ))}
                  </tbody>
              </table>
            </div>
        </div>
      ) : activeTab === 'rekapFormatif' ? (
        <div className="bg-white dark:bg-slate-900 p-8 rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm animate-fade-in-up">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <h3 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Rekap Capaian Formatif</h3>
                    <select value={rekapFormatifClass} onChange={e => setRekapFormatifClass(e.target.value)} className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl font-black text-xs uppercase tracking-widest outline-none hover:border-slate-300 dark:hover:border-slate-600 transition-colors focus:ring-2 focus:ring-indigo-500/20">
                        {myClasses.map(cls => <option key={cls} value={cls}>{cls}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={handlePrintFormatifRekap} className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 font-black rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all text-xs tracking-widest uppercase border border-slate-200 dark:border-slate-800">
                      <Printer size={16} /> Cetak Formatif
                  </button>
                  <button onClick={handlePrintAllFormatifRekap} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 active:scale-95 transition-all text-xs tracking-widest uppercase">
                      <Printer size={16} /> Cetak Semua Kelas
                  </button>
                  <button onClick={handleExportFormatifRekap} className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-black rounded-2xl hover:bg-emerald-700 shadow-lg shadow-emerald-500/20 transition-all text-xs tracking-widest uppercase">
                      <Download size={16} /> Export
                  </button>
                </div>
            </div>
            <div className="overflow-x-auto overflow-y-clip">
                <table className="min-w-full text-center border-separate border-spacing-y-2">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                        <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <th className="px-4 py-4 rounded-l-2xl border-y border-l border-slate-200 dark:border-slate-800">No</th>
                            <th className="px-4 py-4 text-left border-y border-slate-200 dark:border-slate-800">Nama Siswa</th>
                            {rekapitulasiFormatifData.tps.map(tp => <th key={tp.id} className="px-4 py-4 border-y border-slate-200 dark:border-slate-800" title={tp.description}>{tp.code}</th>)}
                            <th className="px-0 py-0 rounded-r-2xl border-y border-r border-slate-200 dark:border-slate-800 w-0"></th>
                        </tr>
                    </thead>
                    <tbody className="text-xs font-bold text-slate-700 dark:text-slate-300">
                        {rekapitulasiFormatifData.rows.map((row, i) => {
                            const student = students.find(s => s.id === row.studentId);
                            const isPerempuan = student?.gender === 'Perempuan';
                            return (
                              <tr key={row.studentId} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-all group">
                                  <td className="px-4 py-4 border-y border-l border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 rounded-l-2xl text-slate-400">{i + 1}</td>
                                  <td className={`px-4 py-4 text-left border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 font-extrabold ${isPerempuan ? 'text-rose-600 dark:text-rose-400' : 'text-slate-800 dark:text-slate-200'}`}>{row.studentName.toUpperCase()}</td>
                                  {rekapitulasiFormatifData.tps.map(tp => (
                                      <td key={tp.id} className={`px-4 py-4 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 ${getLevelColor(row.levels[tp.id])} font-black`}>{row.levels[tp.id]}</td>
                                  ))}
                                  <td className="px-0 py-0 rounded-r-2xl border-y border-r border-slate-100 dark:border-slate-800/50 w-0 group-hover:border-slate-200 dark:group-hover:border-slate-700"></td>
                              </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
      ) : (
          <div className="bg-white dark:bg-slate-900 p-8 rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-sm animate-fade-in-up">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <h3 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">Rangking Pararel</h3>
                <div className="flex items-center gap-3">
                  <button onClick={handlePrintRangking} className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 font-black rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all text-xs tracking-widest uppercase border border-slate-200 dark:border-slate-800">
                      <Printer size={16} /> Cetak Rangking
                  </button>
                  <button onClick={handleExportRangking} className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 shadow-lg shadow-indigo-500/20 transition-all text-xs tracking-widest uppercase">
                      <Download size={16} /> Export
                  </button>
                </div>
              </div>

              {/* Grafik Penyebaran Nilai & Ringkasan Statistik */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 p-6 rounded-3xl flex flex-col justify-between">
                      <div>
                          <h4 className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-4">Statistik Rapor Pararel</h4>
                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider block">Jumlah Siswa</span>
                                  <span className="text-xl font-extrabold text-slate-800 dark:text-white">{rangkingData.length}</span>
                              </div>
                              <div>
                                  <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider block">Rata-rata Nilai</span>
                                  <span className="text-xl font-extrabold text-slate-800 dark:text-white">
                                      {rangkingData.length > 0 
                                          ? (rangkingData.reduce((acc, s) => acc + s.finalScore, 0) / rangkingData.length).toFixed(1)
                                          : '0'
                                      }
                                  </span>
                              </div>
                              <div>
                                  <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider block">Nilai Tertinggi</span>
                                  <span className="text-xl font-extrabold text-emerald-600 dark:text-emerald-400">
                                      {rangkingData.length > 0 
                                          ? Math.max(...rangkingData.map(s => s.roundedFinalScore)) 
                                          : '0'
                                      }
                                  </span>
                              </div>
                              <div>
                                  <span className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider block">Nilai Terendah</span>
                                  <span className="text-xl font-extrabold text-rose-600 dark:text-rose-400">
                                      {rangkingData.length > 0 
                                          ? Math.min(...rangkingData.map(s => s.roundedFinalScore)) 
                                          : '0'
                                      }
                                  </span>
                              </div>
                          </div>
                      </div>
                  </div>

                  <div className="lg:col-span-2 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 p-6 rounded-3xl h-[200px]">
                      <h4 className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2">Grafik Penyebaran Nilai Rapor</h4>
                      <div className="w-full h-[120px]">
                          <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={distributionData} margin={{ top: 10, right: 10, left: -25, bottom: 0 }}>
                                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" className="dark:stroke-slate-700/50" />
                                  <XAxis dataKey="range" tick={{ fontSize: 10, fontWeight: 'bold' }} stroke="#94a3b8" />
                                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fontWeight: 'bold' }} stroke="#94a3b8" />
                                  <Tooltip 
                                      cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} 
                                      contentStyle={{ fontSize: '11px', borderRadius: '12px', padding: '8px', border: '1px solid #e2e8f0' }}
                                  />
                                  <Bar dataKey="count" radius={[4, 4, 0, 0]} label={(props: any) => {
                                      const { x, y, width, value } = props;
                                      if (!value || value === 0) return null;
                                      return (
                                          <text x={x + width / 2} y={y - 4} fill="#64748b" textAnchor="middle" fontSize={9} fontWeight="bold">
                                              {value}
                                          </text>
                                      );
                                  }}>
                                      {distributionData.map((entry, index) => (
                                          <Cell key={`cell-${index}`} fill={entry.color} />
                                      ))}
                                  </Bar>
                              </BarChart>
                          </ResponsiveContainer>
                      </div>
                  </div>
              </div>

              <div className="overflow-x-auto overflow-y-clip">
                <table className="min-w-full text-center border-separate border-spacing-y-2">
                    <thead className="bg-slate-50 dark:bg-slate-800/50">
                        <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <th className="px-4 py-4 rounded-l-2xl border-y border-l border-slate-200 dark:border-slate-800">Rank</th>
                            <th onClick={() => handleRangkingSort('name')} className="px-4 py-4 text-left border-y border-slate-200 dark:border-slate-800 cursor-pointer hover:text-indigo-600 transition-colors group">
                                <div className="flex items-center gap-1">Nama Siswa <ArrowUpDown size={10} className={rangkingSortConfig.key === 'name' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} /></div>
                            </th>
                            <th className="px-4 py-4 border-y border-slate-200 dark:border-slate-800">Kelas</th>
                            <th onClick={() => handleRangkingSort('sas')} className="px-4 py-4 border-y border-slate-200 dark:border-slate-800 cursor-pointer hover:text-indigo-600 transition-colors group">
                                <div className="flex items-center justify-center gap-1">Nilai S.Akhir (Evaluasi) <ArrowUpDown size={10} className={rangkingSortConfig.key === 'sas' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} /></div>
                            </th>
                            <th onClick={() => handleRangkingSort('final')} className="px-4 py-4 border-y border-slate-200 dark:border-slate-800 cursor-pointer hover:text-indigo-600 transition-colors group">
                                <div className="flex items-center justify-center gap-1">Nilai Akhir <ArrowUpDown size={10} className={rangkingSortConfig.key === 'final' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} /></div>
                            </th>
                            <th onClick={() => handleRangkingSort('roundedFinal')} className="px-4 py-4 rounded-r-2xl border-y border-r border-slate-200 dark:border-slate-800 cursor-pointer hover:text-indigo-600 transition-colors group">
                                <div className="flex items-center justify-center gap-1">Nilai Rapor <ArrowUpDown size={10} className={rangkingSortConfig.key === 'roundedFinal' ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} /></div>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="text-xs font-bold text-slate-700 dark:text-slate-300">
                        {rangkingData.map((s, i) => (
                            <tr key={s.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-all group">
                                <td className="px-4 py-4 border-y border-l border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 rounded-l-2xl text-slate-400 font-black text-base">{i + 1}</td>
                                <td className={`px-4 py-4 text-left border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 font-extrabold ${s.gender === 'Perempuan' ? 'text-rose-600 dark:text-rose-400' : 'text-slate-800 dark:text-slate-200'}`}>{s.name.toUpperCase()}</td>
                                <td className="px-4 py-4 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 text-indigo-600 text-base font-black">{s.class}</td>
                                <td className="px-4 py-4 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 font-black text-green-600 text-base">{typeof s.sasScore === 'number' ? s.sasScore : '-'}</td>
                                <td className="px-4 py-4 border-y border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 font-black text-amber-600 text-base">{s.finalScore}</td>
                                <td className="px-4 py-4 border-y border-r border-slate-100 dark:border-slate-800/50 group-hover:border-slate-200 dark:group-hover:border-slate-700 rounded-r-2xl font-black text-rose-600 bg-slate-50/50 dark:bg-slate-800/50 group-hover:bg-transparent text-lg">{s.roundedFinalScore}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
              </div>
          </div>
      )}
    </div>
  );
};

export default NilaiRaporView;
